import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { message, Modal, Button, Image, Divider, Tooltip, Popconfirm, Tag, Avatar, Popover, Input, Checkbox } from 'antd';
import { config } from "util/config";
import { httpClient } from "util/Api";
import CustomBadge from 'components/CustomBadge';
import CompanyUserAvatar from 'components/CompanyUserAvatar';
import CustomImage from 'components/CustomImage';
import { operationalFunction } from 'components/UIBuilder/SectionFunction/Common';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { ExclamationCircleOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import JSZip from 'jszip';

const DOCUMENT_NAME_MAX_LENGTH = 4000;

// Service Request Files 列的单个文件标签：名称直接在行内编辑
function ServiceRequestDocumentFile({ item, sectionComponent, pageComponent }) {
  const extensionIndex = (item.DocumentName || '').lastIndexOf('.');
  const documentExtension = extensionIndex > 0 ? item.DocumentName.substring(extensionIndex) : '';
  const originalName = extensionIndex > 0 ? item.DocumentName.substring(0, extensionIndex) : (item.DocumentName || '');
  const [editing, setEditing] = useState(false);
  const [documentName, setDocumentName] = useState(originalName);

  const saveDocumentName = () => {
    const newName = documentName.trim();
    if (!newName) {
      message.warning('Please enter a document name.');
      return;
    }
    httpClient.put('/Business/ServiceRequest/RenameDocument', {
      ServiceRequestDocumentId: item.ServiceRequestDocumentId,
      DocumentName: newName
    }).then(({ data }) => {
      if (!data.result) {
        message.error(data.message || 'Rename failed.');
        return;
      }
      message.success('Document renamed.');
      setEditing(false);
      sectionComponent?.getSectionDataByDefault();
    }).catch(() => message.error('Rename failed.'));
  };

  const cancelEditing = () => {
    setDocumentName(originalName);
    setEditing(false);
  };

  return <div>
    <span className='inline-document-span' style={{
      color: '#007bff', border: '1px solid #007bff', fontSize: '0.8rem', padding: '0.25em 0.4em',
      borderRadius: '0.25rem', display: 'inline-flex', alignItems: 'center', verticalAlign: 'baseline',
      lineHeight: '18px', margin: '0 6px 6px 0', cursor: 'pointer'
    }}>
      {editing
        ? <><Input value={documentName} maxLength={Math.max(1, DOCUMENT_NAME_MAX_LENGTH - documentExtension.length)} autoFocus size='small' style={{ width: '180px' }} onChange={(event) => setDocumentName(event.target.value)} onPressEnter={saveDocumentName} /><span>{documentExtension}</span></>
        : <span onClick={() => { download({ url: '/Business/Document/Download/' + item.DocumentId + '?filename=' + formatFileNameForUrl(item.DocumentName), data: null, filename: item.DocumentName }) }}>{item.DocumentName}</span>}
      <span style={{ margin: '0 4px' }}>|</span>
      {editing
        ? <><Tooltip title="Save"><CheckOutlined style={{ fontSize: '16px' }} onClick={saveDocumentName} /></Tooltip><span style={{ margin: '0 4px' }}>|</span><Tooltip title="Cancel"><CloseOutlined style={{ fontSize: '16px' }} onClick={cancelEditing} /></Tooltip></>
        : <Tooltip title="Rename file"><EditOutlined style={{ fontSize: '16px' }} onClick={() => setEditing(true)} /></Tooltip>}
      <span style={{ margin: '0 4px' }}>|</span>
      <Popconfirm placement="bottom" title={config.component.defaultDeleteConfirmText} onConfirm={() => { operationalFunction({ functionName: 'deleteServiceRequestDocument', sectionName: 'ServiceRequest', parameter: { ServiceRequestDocumentId: item.ServiceRequestDocumentId }, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}>
        <DeleteOutlined style={{ fontSize: '18px' }} />
      </Popconfirm>
    </span>
  </div>;
}

//设置token
export function setToken(token): void {
  localStorage.setItem('token', token);
}

//取得token
export function getToken() {
  return localStorage.getItem('token');
}

//原字符串, 保留长度, 超出时的省略字符
export function truncateString(str, length, ellipsis) {
  ellipsis = ellipsis || '';
  return length !== undefined && str !== undefined && str !== null ? str.substring(0, length) + (length < str.length ? ellipsis : '') : str;
}

export function roundToDecimalPlaces(number, places) {
  // 确保 places 是一个整数
  places = Math.trunc(places);

  // 处理无效输入
  if (isNaN(number) || !isFinite(number)) {
    return NaN;
  }

  // 记录原始符号
  const sign = Math.sign(number);

  // 使用绝对值进行计算
  number = Math.abs(number);

  // 处理四舍五入的情况
  const factor = Math.pow(10, places);
  const roundedNumber = Math.round((number + Number.EPSILON) * factor) / factor;

  // 处理小数位数不足的情况，并恢复原始符号
  return sign * Number(roundedNumber.toFixed(places));
}

//原数值, 保留几位小数, 货币符号, 千位分隔符, 小数分隔符
export function formatMoney(number, places, symbol, thousand, decimal) {
  // 确定是否是负数，并在处理过程中使用绝对值
  let isNegative = number < 0;
  if (number < 0 && number > -0.0001) {
    number = 0;
  }
  places = !isNaN(places = Math.abs(places)) ? places : 2;
  symbol = symbol || '$';
  thousand = thousand || ',';
  decimal = decimal || '.';

  // 对数字进行四舍五入，并转换为整数部分的字符串形式
  let roundedNumber = roundToDecimalPlaces(Math.abs(number), places);
  let i = parseInt(roundedNumber, 10) + '';

  // 处理千分位分隔符
  let j = i.length > 3 ? i.length % 3 : 0;
  let formattedNumber = (j ? i.substr(0, j) + thousand : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousand);

  // 添加小数点及小数部分
  let decimalPart = (roundedNumber - parseInt(roundedNumber, 10)).toFixed(places).slice(2);
  formattedNumber += places ? decimal + decimalPart : '';

  // 添加货币符号和负号
  return symbol + (isNegative ? '-' : '') + formattedNumber;
}

//为货币控件格式化显示
export function formatMoneyForControl(value, info, places, allowNegative) {
  allowNegative = allowNegative || false;
  if (isNullOrEmpty(value)) {
    return '';
  }

  // 删除非数字字符
  if (allowNegative) {
    value = value.replace(/[^\d-.]/g, '');
  } else {
    value = value.replace(/[^\d.]/g, '');
  }

  if (info.userTyping) {
    // 查找字符串中的第一个小数点的位置
    const dotIndex = value.indexOf('.');
    if (dotIndex >= 0) {
      // 如果存在小数点，只保留小数点后两位，并删除其他的小数点
      value = value.substr(0, dotIndex + (places + 1)).replace(/\.(?=.*\.)/g, '');
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  } else {
    return `${value !== undefined ? parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places }) : ''}`;
  }
  return parseFloat(value);
}

//为货币控件格式化数值
export function parserMoneyForControl(value, allowNegative) {
  allowNegative = allowNegative || false;
  if (!allowNegative) {
    return parseFloat(value.replace(/[-,]/g, ''));
  } else {
    return parseFloat(value.replace(/[,]/g, ''));
  }
}

//数值格式化成文件大小
export function formatFileSize(fileSize, places) {
  places = places || 1;
  if (fileSize === 0) return '0 Bytes';
  const k = 1024;
  const dm = places < 0 ? 0 : places;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(fileSize) / Math.log(k));
  return parseFloat((fileSize / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

//字符串转DateTime, 当前为澳洲日期格式, 根据项目改写此方法
export function stringToDateTime(str) {
  try {
    var type = str.split(' ');
    var d = type[0].split('/');
    var t = type[1].split(':');
    return new Date(d[2], d[1] - 1, d[0], t[0] !== undefined ? t[0] : 0, t[1] !== undefined ? t[1] : 0, t[2] !== undefined ? t[2] : 0);
  } catch {
    return new Date(1970, 0, 1);
  }
}

//获取年月日的时间戳
export function formatDateToVersion() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 因为月份是从0开始的，所以需要+1
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

//根据 uie.titleColor 渲染 Form.Item / Descriptions.Item 的 label; 为空时返回原标题字符串
export function getUieLabel(uie) {
  if (uie && uie.titleColor) {
    return <span style={{ color: uie.titleColor }}>{uie.title}</span>;
  }
  return uie ? uie.title : '';
}

//记录该 Document section 下已打开过的文档
export function markDocumentOpened(sectionComponent, key) {
  const documentTable = sectionComponent?.child?.current;
  if (!documentTable) return;
  documentTable.setState(previousState => {
    // 部分复用 Document 链接的 section 未预先初始化该字段，首次点击时按空数组处理。
    const openedDocumentKeys = Array.isArray(previousState?.openedDocumentKeys)
      ? previousState.openedDocumentKeys
      : [];
    const stateData = Array.isArray(previousState?.data) ? previousState.data : [];
    let dataChanged = false;

    // DocumentTable 使用文件夹数组，SubJobDocumentTable 直接使用文档数组；这里统一兼容两种结构。
    const updateDocument = document => {
      if (document?.key === key && document.NotOpend === true) {
        dataChanged = true;
        return { ...document, NotOpend: false };
      }
      return document;
    };
    const data = stateData.map(item => {
      if (Array.isArray(item?.DocumentList)) {
        let folderChanged = false;
        const documentList = item.DocumentList.map(document => {
          const updatedDocument = updateDocument(document);
          folderChanged = folderChanged || updatedDocument !== document;
          return updatedDocument;
        });
        return folderChanged ? { ...item, DocumentList: documentList } : item;
      }
      return updateDocument(item);
    });

    const keysChanged = openedDocumentKeys.indexOf(key) === -1;
    if (!keysChanged && !dataChanged) return null;

    return {
      ...(keysChanged ? { openedDocumentKeys: [...openedDocumentKeys, key] } : {}),
      ...(dataChanged ? { data } : {})
    };
  });
}

//根据每个单字段的类型, 对生成单元格的数据进行格式化
export function formatUieData({ uie, data, record, companyId, sectionComponent, pageComponent, key }) {
  if (key === undefined || key === null) {
    key = record.key;
  }
  if (uie.key !== null) {
    key = record[uie.key];
  }
  return record.className === undefined ? getUieData({ uie, data, record, companyId, sectionComponent, pageComponent, key }) : <span className={record.className}>{getUieData({ uie, data, record, companyId, sectionComponent, pageComponent, key })}</span>
}

// 检测是否为移动或平板设备
export function isMobileOrTablet() {
  // 非浏览器环境直接返回false
  if (typeof window === 'undefined') {
    return false;
  }

  // 检测触摸支持
  const isTouchDevice = 'ontouchstart' in window ||
    (window.navigator && window.navigator.maxTouchPoints > 0);

  // 检测iOS设备（包括iPad各种模式）
  const isIOSDevice = /iPhone|iPod|iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && isTouchDevice);

  // 检测Android设备
  const isAndroidDevice = /Android/.test(navigator.userAgent) &&
    !/Windows Phone/.test(navigator.userAgent);

  // 检测小屏幕设备（平板和手机典型尺寸）
  const isSmallScreen = window.innerWidth < 1024;

  // 综合判断：触摸设备 + (移动设备UA/小屏幕)
  return isTouchDevice && (isIOSDevice || isAndroidDevice || isSmallScreen);
}

function getUieData({ uie, data, record, companyId, sectionComponent, pageComponent, key }) {
  let backgroundColor = ''
  let color = ''
  let className = uie.className == null ? undefined : uie.className;
  let result = false;
  let mobileReg = new RegExp(/^(04|\+614)\d{8}$/);

  switch (uie.type) {
    case 'date':
      return <>{truncateString(data, 10)}</>
    case 'dateBadge':
      return data !== null ? <CustomBadge text={truncateString(data, 10)} backgroundColor="#00a143" /> : <></>
    case 'bool':
      return data ? 'Yes' : 'No'
    case 'time':
      return data + 'h'
    case 'disable':
      return data ? <CustomBadge text="Disabled" backgroundColor={config.color.danger} /> : <CustomBadge text="Enabled" backgroundColor={config.color.success} />
    case 'active':
      return data ? <CustomBadge text="Active" backgroundColor={config.color.success} /> : <CustomBadge text="Inactive" backgroundColor={config.color.danger} />
    case 'inactive':
      return data ? <CustomBadge text="Inactive" backgroundColor={config.color.danger} /> : <CustomBadge text="Active" backgroundColor={config.color.success} />
    case 'boolHighlightTrue':
      return data ? <CustomBadge text="Yes" backgroundColor={config.color.success} /> : 'No'
    case 'appIntegrationConnected':
      switch (data) {
        case true:
          return <CustomBadge text="Connected" backgroundColor={config.color.success} />
        case false:
          return <CustomBadge text="Disconnected " backgroundColor='#999999' />
        default:
          return <></>
      }
    case 'money':
      return data === null ? <></> : data < 0 ? <span className="red">{formatMoney(data)}</span> : <>{formatMoney(data)}</>
    case 'money3':
      return data === null ? <></> : data < 0 ? <span className="red">{formatMoney(data, 3)}</span> : <>{formatMoney(data, 3)}</>
    case 'fileSize':
      return <>{formatFileSize(data)}</>
    case 'pageLink':
      // lock状态下只显示文字
      if (pageComponent?.state?.jobData && !isNullOrEmpty(pageComponent.state.jobData.LockReason) && sectionComponent?.props?.section?.enableLock) {
        if (record[uie.color] !== null && record[uie.color] !== undefined) {
          return <CustomBadge text={data} backgroundColor={record[uie.color]} />
        } else {
          return <>{data}</>
        }
      }
      // keySource: 链接定位用的 key 取自行内别的字段（如 ServiceRequest 行链到所属 Job 用 keySource="JobId"），
      // 默认仍用行 key；配合 target="_blank" 可在新标签打开
      let pageLinkKey = (uie.keySource !== undefined && uie.keySource !== null && record[uie.keySource] !== undefined && record[uie.keySource] !== null) ? record[uie.keySource] : key;
      if (record[uie.color] !== null && record[uie.color] !== undefined) {
        return <Link target={uie.target} to={{ pathname: uie.href + '/' + pageLinkKey, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: pageLinkKey }}><CustomBadge text={data} backgroundColor={record[uie.color]} /></Link>
      } else {
        return <Link target={uie.target} to={{ pathname: uie.href + '/' + pageLinkKey, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: pageLinkKey }}>{data}</Link>
      }
    case 'clientTrackingPageLink':
      return <>
        <Link title='Click to Open' target="_blank" to={{ pathname: config.clientUrl + '/job/public/' + record.AccessCode, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
        <i className="fal fa-copy ml-2"
          style={{ cursor: 'pointer' }}
          onClick={async (e: any) => {
            let text = config.clientUrl + '/job/public/' + record.AccessCode;
            message.info('Copied to clipboard!');
            await navigator.clipboard.writeText(text);
          }}
          title="Copy link"></i>
      </>
    case 'clientTrackingPageLinkForClient':
      return <>
        <Link title='Click to Open' target="_blank" to={{ pathname: config.clientUrl + '/job/public-for-client/' + record.ClientAccessCode, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
        <i className="fal fa-copy ml-2"
          style={{ cursor: 'pointer' }}
          onClick={async (e: any) => {
            let text = config.clientUrl + '/job/public-for-client/' + record.ClientAccessCode;
            message.info('Copied to clipboard!');
            await navigator.clipboard.writeText(text);
          }}
          title="Copy link"></i>
      </>
    case 'clientSubJobTrackingPageLink':
      return <>
        <Link title='Click to Open' target="_blank" to={{ pathname: config.clientUrl + '/sub-job/public/' + record.AccessCode, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
        <i className="fal fa-copy ml-2"
          style={{ cursor: 'pointer' }}
          onClick={async (e: any) => {
            let text = config.clientUrl + '/sub-job/public/' + record.AccessCode;
            message.info('Copied to clipboard!');
            await navigator.clipboard.writeText(text);
          }}
          title="Copy link"></i>
      </>
    case 'jobHyperLink':
      return data !== null && data !== undefined ? <>
        <Link title='Click to Open' target="_blank" to={{ pathname: data.indexOf('http') < 0 ? 'http://' + data : data, key: key }}>{data}</Link>
        <i className="fal fa-copy ml-2"
          style={{ cursor: 'pointer' }}
          onClick={async (e: any) => {
            message.info('Copied to clipboard!');
            await navigator.clipboard.writeText(data);
          }}
          title="Copy link"></i>
      </> : <></>
    case 'document':
      switch (record.Name.split('.').pop().toLowerCase()) {
        case 'pdf':
        case 'jpeg':
        case 'jpg':
        case 'png':
          return <span className='document-name-span'>
            {
              record.NotOpend === true ?
                <span
                  className="not-opend-inline-document-span"
                  id={'inline-document-span-' + record.key}
                  style={{
                    color: '#007bff',
                    border: '1px solid #007bff',
                    fontSize: '0.8rem',
                    padding: '0.25em 0.4em',
                    borderRadius: '0.25rem',
                    display: 'inline-flex',
                    verticalAlign: 'baseline',
                    lineHeight: '18px',
                    margin: '0 6px 6px 0',
                    cursor: 'pointer'
                  }}>
                  <span
                    onClick={(e) => {
                      if (e.button === 0 || e.button === 1) {
                        markDocumentOpened(sectionComponent, record.key); // 标记并高亮已打开的文档行
                      }
                    }}
                    onAuxClick={(e) => {
                      if (e.button === 0 || e.button === 1) {
                        markDocumentOpened(sectionComponent, record.key); // 标记并高亮已打开的文档行
                      }
                    }}
                  ><a style={{ color: '#ffffff' }} href={'/download/Business/Document/Download/' + key + '?filename=' + formatFileNameForUrl(record.Name)} target='_blank'>{data}</a></span>
                </span> : record.Stamped === true ? <span style={{ cursor: 'pointer' }}><CustomBadge text={<a onClick={() => markDocumentOpened(sectionComponent, record.key)} href={'/download/Business/Document/Download/' + key + '?filename=' + formatFileNameForUrl(record.Name)} style={{ color: '#ffffff' }} target='_blank'>{data}</a>} backgroundColor={pageComponent.state.stampedDocColor} /></span> : <span><a onClick={() => markDocumentOpened(sectionComponent, record.key)} href={'/download/Business/Document/Download/' + key + '?filename=' + formatFileNameForUrl(record.Name)} target='_blank'>{data}</a></span>}
            <div
              style={{ float: 'right', width: '30px' }}
            >
              <Button
                icon={<i className="icon icon-menu ant-menu-item-icon"></i>}
                type='link'
                className='image-button-div-cion document-name-icon'
                onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showDocumentContextMenu', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
              />
            </div>
          </span>
        default:
          return <span className='document-name-span'>
            {
              record.NotOpend === true ?
                <span
                  className="not-opend-inline-document-span"
                  id={'inline-document-span-' + record.key}
                  style={{
                    color: '#007bff',
                    border: '1px solid #007bff',
                    fontSize: '0.8rem',
                    padding: '0.25em 0.4em',
                    borderRadius: '0.25rem',
                    display: 'inline-flex',
                    verticalAlign: 'baseline',
                    lineHeight: '18px',
                    margin: '0 6px 6px 0',
                    cursor: 'pointer'
                  }}>
                  <span onClick={() => {
                    markDocumentOpened(sectionComponent, record.key); // 标记并高亮已打开的文档行
                    download({ url: '/Business/Document/Download/' + record.key, data: null, filename: record.Name });
                    const el = document.getElementById('inline-document-span-' + record.key);
                    if (el) {
                      el.className = 'inline-document-span';
                    }
                  }}>{record.Name}</span>
                </span> :
                record.Stamped === true ? <span style={{ cursor: 'pointer' }} onClick={() => { markDocumentOpened(sectionComponent, record.key); download({ url: '/Business/Document/Download/' + key, data: null, filename: data }) }}><CustomBadge text={data} backgroundColor={pageComponent.state.stampedDocColor} /></span> : <span className='link' onClick={() => { markDocumentOpened(sectionComponent, record.key); download({ url: '/Business/Document/Download/' + key, data: null, filename: data }) }}>{data}</span>}
            <div
              style={{ float: 'right', width: '30px' }}
            >
              <Button
                icon={<i className="icon icon-menu ant-menu-item-icon"></i>}
                type='link'
                className='image-button-div-cion document-name-icon'
                onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showDocumentContextMenu', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
              />
            </div>
          </span>
      }
    case 'documentType':
      switch (record.Name.split('.').pop().toLowerCase()) {
        case 'pdf':
        case 'jpeg':
        case 'jpg':
        case 'png':
          return <a href={'/download/Business/Document/Download/' + key + '?filename=' + formatFileNameForUrl(record.Name)} target='_blank'>{data}</a>
        default:
          return <span className='link' onClick={() => { download({ url: '/Business/Document/Download/' + key, data: null, filename: record.Name }) }}>{data}</span>
      }
    case 'documentShare':
      let html = '';
      if (record.ShareClientPortal) {
        html += '<span class="circular" style="background: #00A143; margin-right: .25rem;" title="Client">C</span>';
      }
      if (record.ShareTrackingPage) {
        html += '<span class="circular" style="background: #E91E63; margin-right: .25rem;" title="Tracking Page">T</span>';
      }
      if (record.ShareOnsiteJob) {
        html += '<span class="circular" style="background: #2196F3; margin-right: .25rem;" title="Onsite Job">O</span>';
      }
      return <span dangerouslySetInnerHTML={{ __html: html }} />
    case 'badge':
      if (uie.color.indexOf('#') == 0) {
        backgroundColor = uie.color;
      } else {
        backgroundColor = record[uie.color];
      }
      if (uie.fontColor && uie.fontColor.indexOf('#') == 0) {
        color = uie.fornColor;
      } else {
        color = record[uie.fontColor];
      }
      switch (uie.component) {
        case 'Button':
          return <><Button
            icon={<CustomBadge text={data} backgroundColor={backgroundColor} color={color} className={className} />}
            type="link"
            style={{ width: 'auto' }}
            onClick={(e: any) => { operationalFunction({ e: e, functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
            onContextMenu={(e: any) => { operationalFunction({ e: e, functionName: uie.onContextMenu, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
          /> {record.Loading === true ? <img className="loading-icon-inline" src="/assets/images/icon/loading.svg" /> : <></>}</>
        case 'html':
          return data !== null && data !== undefined ? data === 'loading-icon' ? <img className="loading-icon-inline" src="/assets/images/icon/loading.svg" /> : <CustomBadge text={<span dangerouslySetInnerHTML={{ __html: data.replace(/\n/g, '<br />') }}></span>} display='inline-block' backgroundColor={backgroundColor} color={color} className={className} /> : <></>
        default:
          return data !== null && data !== undefined ? data === 'loading-icon' ? <img className="loading-icon-inline" src="/assets/images/icon/loading.svg" /> : <CustomBadge text={data} backgroundColor={backgroundColor} color={color} className={className} /> : <></>
      }
    case 'badgeWithBackgroundColor':
      if (uie.color.indexOf('#') == 0) {
        backgroundColor = uie.color;
      } else {
        backgroundColor = record[uie.color];
      }
      if (uie.fontColor && uie.fontColor.indexOf('#') == 0) {
        color = uie.fornColor;
      } else {
        color = record[uie.fontColor];
      }
      if (backgroundColor == null) {
        return data;
      } else {
        return <CustomBadge text={data} backgroundColor={backgroundColor} color={color} className={className} />
      }
    case 'html':
      // return record.edit === true ?
      //   <span className='full-in-parent'>
      //     <CKEditor
      //       data={data}
      //       editor={DecoupledEditor}
      //       onReady={editor => {
      //         editor.ui.getEditableElement().parentElement.insertBefore(
      //           editor.ui.view.toolbar.element,
      //           editor.ui.getEditableElement(),
      //           editor.editing.view.focus()
      //         );
      //       }}
      //       onChange={(event, editor) => {
      //       }}
      //       onBlur={(event, editor) => {
      //         record[uie.source] = editor.getData();
      //         operationalFunction({ functionName: uie.onChange, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
      //       }}
      //       onFocus={(event, editor) => {
      //       }}
      //       config={config.component.CKEditor.config}
      //     />
      //   </span> :
      //   <span className='full-in-parent'
      //     onClick={() => { operationalFunction({ functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
      //     dangerouslySetInnerHTML={{ __html: data }} />
      return <span className='full-in-parent'
        onClick={() => { operationalFunction({ functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
        dangerouslySetInnerHTML={{ __html: data }} />
    case 'commaWrap':
      return <span dangerouslySetInnerHTML={{ __html: data.replace(/,/g, '<br />') }}></span>
    case 'JobChecklistNote':
      return <>
        {
          record.editNote === true ?
            <span className='full-in-parent'>
              <CKEditor
                data={data}
                editor={DecoupledEditor}
                onReady={editor => {
                  editor.editing.view.change(writer => {
                    writer.setAttribute('spellcheck', 'true', editor.editing.view.document.getRoot());
                  });
                  // 等用户点击编辑区取得焦点，避免脚本聚焦使浏览器跳过拼写检查。
                  editor.ui.getEditableElement().parentElement.insertBefore(
                    editor.ui.view.toolbar.element,
                    editor.ui.getEditableElement()
                  );

                  // 保存初始数据
                  const initialData = editor.getData();
                  let isDirty = false;

                  // 监听内容变化
                  editor.model.document.on('change:data', () => {
                    const currentData = editor.getData();
                    isDirty = currentData !== initialData;
                  });

                  // 创建自定义按钮
                  const saveButton = document.createElement('button');
                  saveButton.innerText = 'Save';
                  saveButton.style.border = '1px solid #03a9f4'; // 边框
                  saveButton.style.color = '#03a9f4'; // 文字颜色
                  saveButton.style.padding = '0px 4px'; // 内边距
                  saveButton.style.rightLeft = '10px'; // 左边距
                  saveButton.style.cursor = 'pointer'; // 鼠标悬停时显示手型
                  saveButton.style.borderRadius = '0.25rem'; //圆角
                  saveButton.style.marginRight = '10px'
                  saveButton.onclick = () => {
                    record[uie.source] = applyDefaultOlStylesOnlyWhenMissing(editor.getData());
                    operationalFunction({ functionName: 'quickEditNote', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
                  };

                  const cancelButton = document.createElement('button');
                  cancelButton.innerText = 'Cancel';
                  cancelButton.style.border = '1px solid #f5222d'; // 边框
                  cancelButton.style.color = '#f5222d'; // 文字颜色
                  cancelButton.style.padding = '0px 4px'; // 内边距
                  cancelButton.style.rightLeft = '10px'; // 左边距
                  cancelButton.style.cursor = 'pointer'; // 鼠标悬停时显示手型
                  cancelButton.style.borderRadius = '0.25rem'; //圆角
                  cancelButton.onclick = () => {
                    operationalFunction({ functionName: 'cancelEditNote', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
                  };

                  // 将自定义按钮添加到工具栏
                  editor.ui.view.toolbar.element.appendChild(saveButton);
                  editor.ui.view.toolbar.element.appendChild(cancelButton);

                  // 只在整个编辑器失焦时触发
                  editor.ui.focusTracker.on("change:isFocused", (evt, name, isFocused) => {
                    if (!isFocused) {
                      // 判断失焦是真的离开整个编辑器
                      if (pageComponent.props.authUser.Company.InlineEditingAutoSave == true) {
                        record[uie.source] = applyDefaultOlStylesOnlyWhenMissing(editor.getData());
                        operationalFunction({ functionName: 'quickEditNote', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
                        isDirty = false;
                      } else {
                        if (isDirty) {
                          Modal.confirm({
                            title: 'Confirm',
                            content: 'Save Note?',
                            transitionName: "",
                            maskTransitionName: "",
                            onOk: () => {
                              record[uie.source] = applyDefaultOlStylesOnlyWhenMissing(editor.getData());
                              operationalFunction({ functionName: 'quickEditNote', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
                              isDirty = false;
                            },
                          });
                        }
                      }
                    }
                  });
                }}
                onChange={(event, editor) => {
                }}
                onBlur={(event, editor) => {
                }}
                onFocus={(event, editor) => {
                }}
                config={{
                  ckfinder: {
                    // 上传图片到后端的 url。
                    uploadUrl: config.baseUrl + '/System/Global/UploadPublicImage',
                  },
                  link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://'
                  },
                  toolbar: ['fontSize', 'fontColor', 'bold', 'italic', 'underline', 'strikethrough', 'bulletedList', 'numberedList', 'link', 'undo', 'redo'],
                  fontSize: {
                    options: [
                      'default',
                      { model: '8pt', title: '8' },
                      { model: '9pt', title: '9' },
                      { model: '10pt', title: '10' },
                      { model: '11pt', title: '11' },
                      { model: '12pt', title: '12' },
                      { model: '14pt', title: '14' },
                      { model: '16pt', title: '16' },
                      { model: '18pt', title: '18' },
                      { model: '20pt', title: '20' },
                      { model: '22pt', title: '22' },
                      { model: '24pt', title: '24' },
                      { model: '28pt', title: '28' },
                      { model: '32pt', title: '32' },
                      { model: '36pt', title: '36' },
                      { model: '48pt', title: '48' },
                      { model: '72pt', title: '72' }
                    ],
                    supportAllValues: true,
                  },
                }}
              />
            </span> :
            <span className='full-in-parent'
              onClick={() => { operationalFunction({ functionName: uie.onClick, sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
              dangerouslySetInnerHTML={{ __html: data }} />
        }
      </>
    case 'JobChecklistComments':
      const pictureFormat = ['jpg', 'jpeg', 'png', 'bmp'];
      return <>
        {
          record.editComments === true ?
            <span className='full-in-parent'>
              <CKEditor
                data={data}
                editor={DecoupledEditor}
                onReady={editor => {
                  editor.editing.view.change(writer => {
                    writer.setAttribute('spellcheck', 'true', editor.editing.view.document.getRoot());
                  });
                  // 等用户点击编辑区取得焦点，避免脚本聚焦使浏览器跳过拼写检查。
                  editor.ui.getEditableElement().parentElement.insertBefore(
                    editor.ui.view.toolbar.element,
                    editor.ui.getEditableElement()
                  );

                  // 保存初始数据
                  const initialData = editor.getData();
                  let isDirty = false;

                  // 监听内容变化
                  editor.model.document.on('change:data', () => {
                    const currentData = editor.getData();
                    isDirty = currentData !== initialData;
                  });

                  // 创建自定义按钮
                  const saveButton = document.createElement('button');
                  saveButton.innerText = 'Save';
                  saveButton.style.border = '1px solid #03a9f4'; // 边框
                  saveButton.style.color = '#03a9f4'; // 文字颜色
                  saveButton.style.padding = '0px 4px'; // 内边距
                  saveButton.style.rightLeft = '10px'; // 左边距
                  saveButton.style.cursor = 'pointer'; // 鼠标悬停时显示手型
                  saveButton.style.borderRadius = '0.25rem'; //圆角
                  saveButton.style.marginRight = '10px'
                  saveButton.onclick = () => {
                    record[uie.source] = applyDefaultOlStylesOnlyWhenMissing(editor.getData());
                    operationalFunction({ functionName: 'quickEditComments', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
                  };

                  const cancelButton = document.createElement('button');
                  cancelButton.innerText = 'Cancel';
                  cancelButton.style.border = '1px solid #f5222d'; // 边框
                  cancelButton.style.color = '#f5222d'; // 文字颜色
                  cancelButton.style.padding = '0px 4px'; // 内边距
                  cancelButton.style.rightLeft = '10px'; // 左边距
                  cancelButton.style.cursor = 'pointer'; // 鼠标悬停时显示手型
                  cancelButton.style.borderRadius = '0.25rem'; //圆角
                  cancelButton.onclick = () => {
                    operationalFunction({ functionName: 'cancelEditComments', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
                  };

                  // 将自定义按钮添加到工具栏
                  editor.ui.view.toolbar.element.appendChild(saveButton);
                  editor.ui.view.toolbar.element.appendChild(cancelButton);

                  // 只在整个编辑器失焦时触发
                  editor.ui.focusTracker.on("change:isFocused", (evt, name, isFocused) => {
                    if (!isFocused) {
                      // 判断失焦是真的离开整个编辑器
                      if (pageComponent.props.authUser.Company.InlineEditingAutoSave == true) {
                        record[uie.source] = applyDefaultOlStylesOnlyWhenMissing(editor.getData());
                        operationalFunction({ functionName: 'quickEditComments', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
                        isDirty = false;
                      } else {
                        if (isDirty) {
                          Modal.confirm({
                            title: 'Confirm',
                            content: 'Save Comments?',
                            transitionName: "",
                            maskTransitionName: "",
                            onOk: () => {
                              record[uie.source] = applyDefaultOlStylesOnlyWhenMissing(editor.getData());
                              operationalFunction({ functionName: 'quickEditComments', sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
                              isDirty = false;
                            },
                          });
                        }
                      }
                    }
                  });

                }}
                onChange={(event, editor) => {
                }}
                onBlur={(event, editor) => {
                }}
                onFocus={(event, editor) => {
                }}
                config={{
                  ckfinder: {
                    // 上传图片到后端的 url。
                    uploadUrl: config.baseUrl + '/System/Global/UploadPublicImage',
                  },
                  link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://'
                  },
                  toolbar: ['fontSize', 'fontColor', 'bold', 'italic', 'underline', 'strikethrough', 'bulletedList', 'numberedList', 'link', 'undo', 'redo'],
                  fontSize: {
                    options: [
                      'default',
                      { model: '8pt', title: '8' },
                      { model: '9pt', title: '9' },
                      { model: '10pt', title: '10' },
                      { model: '11pt', title: '11' },
                      { model: '12pt', title: '12' },
                      { model: '14pt', title: '14' },
                      { model: '16pt', title: '16' },
                      { model: '18pt', title: '18' },
                      { model: '20pt', title: '20' },
                      { model: '22pt', title: '22' },
                      { model: '24pt', title: '24' },
                      { model: '28pt', title: '28' },
                      { model: '32pt', title: '32' },
                      { model: '36pt', title: '36' },
                      { model: '48pt', title: '48' },
                      { model: '72pt', title: '72' }
                    ],
                    supportAllValues: true,
                  },
                }}
              />
            </span> :
            <span className='full-in-parent'
              onClick={() => { operationalFunction({ functionName: uie.onClick, sectionName: 'JobChecklist', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
              dangerouslySetInnerHTML={{ __html: data }} />
        }
        {
          record.JobChecklistImageList !== null && record.JobChecklistImageList.length > 0 ? <Image.PreviewGroup>
            {
              record.JobChecklistImageList
                .filter(image => {
                  if (!image.Name) return false;

                  const ext = image.Name.split('.').pop().toLowerCase();
                  return pictureFormat.includes(ext);
                })
                .map((image, i) => (
                  <CustomImage
                    key={i}
                    divClassName="job-checklist-image-container"
                    imageClassName="job-checklist-image"
                    src={image.DocumentId}
                    srcType={'DocumentId'}
                    buttons={
                      <Popconfirm
                        placement="bottom"
                        title={config.component.defaultDeleteConfirmText}
                        onConfirm={() => {
                          operationalFunction({
                            functionName: 'deleteJobChecklistImage',
                            sectionName: sectionComponent.props.sectionName,
                            parameter: image,
                            sectionComponent: sectionComponent,
                            pageComponent: pageComponent,
                            uie: uie
                          })
                        }}
                      >
                        <Button
                          icon={<img className="svg-img-btn" src={'assets/images/icon/delete.svg'} />}
                          type='link'
                          className='image-button-div-cion'
                        />
                      </Popconfirm>
                    }
                  />
                ))
            }
          </Image.PreviewGroup> : <></>
        }
      </>
    case 'JobChecklistClientComment':
      if (record.JobChecklistClientDocumentList !== null && record.JobChecklistClientDocumentList.length > 0) {
        return <>
          <>{data}</>
          <div style={{ color: '#9d9d9d' }}>{record.ClientCommentUpdatedBy} {record.ClientCommentUpdatedDatetime}</div>
          <Divider />
          {
            record.JobChecklistClientDocumentList.map((item, i) =>
              <Tooltip key={i} mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title={item.CreatedBy + ' ' + item.DateCreated}>
                <div>
                  <span
                    className={item.NotOpend === true ? 'not-opend-inline-document-span' : 'inline-document-span'}
                    id={'inline-document-span-' + item.key}
                    style={{
                      color: '#007bff',
                      border: '1px solid #007bff',
                      fontSize: '0.8rem',
                      padding: '0.25em 0.4em',
                      borderRadius: '0.25rem',
                      display: 'inline-flex',
                      verticalAlign: 'baseline',
                      lineHeight: '18px',
                      margin: '0 6px 6px 0',
                      cursor: 'pointer'
                    }}>
                    <span
                      onClick={(e) => {
                        if (e.button === 0 || e.button === 1) {
                          const el = document.getElementById('inline-document-span-' + item.key);
                          if (el) {
                            el.className = 'inline-document-span';
                          }
                          item.NotOpend = false;
                          let sectionComponent = getSectionComponent(pageComponent, 'Document');
                          let newData = sectionComponent?.child?.current?.state.data.map(folder => {
                            folder.DocumentList.map(document => {
                              if (document.key === item.key) {
                                document.NotOpend = false;
                              }
                              return document;
                            })
                            return folder;
                          })
                          setTimeout(() => {
                            sectionComponent?.child?.current?.setState({ data: [] });
                            sectionComponent?.child?.current?.setState({ data: newData });
                          }, config.setTimeoutMilliSecond);
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 0 || e.button === 1) {
                          const el = document.getElementById('inline-document-span-' + item.key);
                          if (el) {
                            el.className = 'inline-document-span';
                          }
                          item.NotOpend = false;
                          let sectionComponent = getSectionComponent(pageComponent, 'Document');
                          let newData = sectionComponent?.child?.current?.state.data.map(folder => {
                            folder.DocumentList.map(document => {
                              if (document.key === item.key) {
                                document.NotOpend = false;
                              }
                              return document;
                            })
                            return folder;
                          })
                          setTimeout(() => {
                            sectionComponent?.child?.current?.setState({ data: [] });
                            sectionComponent?.child?.current?.setState({ data: newData });
                          }, config.setTimeoutMilliSecond);
                        }
                      }}
                    ><a style={{ color: item.NotOpend === true ? '#ffffff' : '#007bff' }} href={'/download/Business/Document/Download/' + item.key + '?filename=' + formatFileNameForUrl(item.Name)} target='_blank'>{item.Name}</a></span>
                  </span>
                </div>
              </Tooltip>
            )
          }
        </>
      } else {
        return <>
          <>{data}</>
          <div style={{ color: '#9d9d9d' }}>{record.ClientCommentUpdatedBy} {record.ClientCommentUpdatedDatetime}</div>
        </>;
      }
    case 'JobRequiredCertificateClientDocument':
      if (record.JobRequiredCertificateClientDocumentList !== null && record.JobRequiredCertificateClientDocumentList.length > 0) {
        return <>
          {
            record.JobRequiredCertificateClientDocumentList.map((item, i) =>
              <Tooltip key={i} mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title={item.CreatedBy + ' ' + item.DateCreated}>
                <div>
                  <span
                    className={item.NotOpend === true ? 'not-opend-inline-document-span' : 'inline-document-span'}
                    id={'inline-document-span-' + item.key}
                    style={{
                      color: '#007bff',
                      border: '1px solid #007bff',
                      fontSize: '0.8rem',
                      padding: '0.25em 0.4em',
                      borderRadius: '0.25rem',
                      display: 'inline-flex',
                      verticalAlign: 'baseline',
                      lineHeight: '18px',
                      margin: '0 6px 6px 0',
                      cursor: 'pointer'
                    }}>
                    <span
                      onClick={(e) => {
                        if (e.button === 0 || e.button === 1) {
                          const el = document.getElementById('inline-document-span-' + item.key);
                          if (el) {
                            el.className = 'inline-document-span';
                          }
                          let sectionComponent = getSectionComponent(pageComponent, 'Document');
                          let newData = sectionComponent?.child?.current?.state.data.map(folder => {
                            folder.DocumentList.map(document => {
                              if (document.key === item.key) {
                                document.NotOpend = false;
                              }
                              return document;
                            })
                            return folder;
                          })
                          setTimeout(() => {
                            sectionComponent?.child?.current?.setState({ data: [] });
                            sectionComponent?.child?.current?.setState({ data: newData });
                          }, config.setTimeoutMilliSecond);
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 0 || e.button === 1) {
                          const el = document.getElementById('inline-document-span-' + item.key);
                          if (el) {
                            el.className = 'inline-document-span';
                          }
                          let sectionComponent = getSectionComponent(pageComponent, 'Document');
                          let newData = sectionComponent?.child?.current?.state.data.map(folder => {
                            folder.DocumentList.map(document => {
                              if (document.key === item.key) {
                                document.NotOpend = false;
                              }
                              return document;
                            })
                            return folder;
                          })
                          setTimeout(() => {
                            sectionComponent?.child?.current?.setState({ data: [] });
                            sectionComponent?.child?.current?.setState({ data: newData });
                          }, config.setTimeoutMilliSecond);
                        }
                      }}
                    ><a style={{ color: item.NotOpend === true ? '#ffffff' : '#007bff' }} href={'/download/Business/Document/Download/' + item.key + '?filename=' + formatFileNameForUrl(item.Name)} target='_blank'>{item.Name}</a></span>
                  </span>
                </div>
              </Tooltip >
            )
          }
        </>
      } else {
        return <>{data}</>;
      }
    case 'SubJobChecklistComments':
      if (record.SubJobChecklistImageList !== null && record.SubJobChecklistImageList.length > 0) {
        return <>
          <span className='full-in-parent' dangerouslySetInnerHTML={{ __html: data }} />
          <Image.PreviewGroup>
            {
              record.SubJobChecklistImageList.map((image, i) => {
                return <CustomImage key={i}
                  divClassName="subjob-checklist-image-container"
                  imageClassName="subjob-checklist-image"
                  src={getByteImageSrc(image)}
                  // src={image.DocumentId}
                  // srcType={'DocumentId'}
                  buttons={
                    <>
                      <Button
                        icon={<img className="svg-img-btn" src={image.ShowOnReport ? 'assets/images/icon/verified_green.svg' : 'assets/images/icon/verified.svg'} />}
                        type='link'
                        className='image-button-div-cion'
                        onClick={() => { operationalFunction({ functionName: 'changeSubJobChecklistImageShowOnReport', sectionName: sectionComponent.props.sectionName, parameter: image, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
                      />
                      <Popconfirm
                        placement="bottom"
                        title={config.component.defaultDeleteConfirmText}
                        onConfirm={() => { operationalFunction({ functionName: 'deleteSubJobChecklistImage', sectionName: sectionComponent.props.sectionName, parameter: image, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
                      >
                        <Button
                          icon={<img className="svg-img-btn" src={'assets/images/icon/delete.svg'} />}
                          type='link'
                          className='image-button-div-cion'
                        />
                      </Popconfirm>
                    </>}
                />
              })
            }
          </Image.PreviewGroup>
        </>
      } else {
        return <span className='full-in-parent' dangerouslySetInnerHTML={{ __html: data }} />;
      }
    case 'SubJobChecklistClientComment': {
      if (Array.isArray(record?.SubJobChecklistClientDocumentList) && record.SubJobChecklistClientDocumentList.length > 0) {
        return <>
          <>{data}</>
          <div style={{ color: '#9d9d9d' }}>{record.ClientCommentUpdatedBy} {record.ClientCommentUpdatedDatetime}</div>
          <Divider />
          {
            record.SubJobChecklistClientDocumentList.map((item, i) =>
              <Tooltip key={i} mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title={item.CreatedBy + ' ' + item.DateCreated}>
                <div>
                  <span
                    className={item.NotOpend === true ? 'not-opend-inline-document-span' : 'inline-document-span'}
                    id={'inline-document-span-' + item.key}
                    style={{
                      color: '#007bff',
                      border: '1px solid #007bff',
                      fontSize: '0.8rem',
                      padding: '0.25em 0.4em',
                      borderRadius: '0.25rem',
                      display: 'inline-flex',
                      verticalAlign: 'baseline',
                      lineHeight: '18px',
                      margin: '0 6px 6px 0',
                      cursor: 'pointer'
                    }}>
                    <span
                      onClick={(e) => {
                        if (e.button === 0 || e.button === 1) {
                          const el = document.getElementById('inline-document-span-' + item.key);
                          if (el) {
                            el.className = 'inline-document-span';
                          }
                          item.NotOpend = false;
                          const documentSection = getSectionComponent(pageComponent, 'Document');
                          // SubJob 的 Document section 直接保存文档数组，统一方法同时兼容文件夹结构。
                          markDocumentOpened(documentSection, item.key);
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 0 || e.button === 1) {
                          const el = document.getElementById('inline-document-span-' + item.key);
                          if (el) {
                            el.className = 'inline-document-span';
                          }
                          item.NotOpend = false;
                          const documentSection = getSectionComponent(pageComponent, 'Document');
                          // 中键打开时同样同步已读状态，并保护尚未加载的 Document section。
                          markDocumentOpened(documentSection, item.key);
                        }
                      }}
                    ><a style={{ color: item.NotOpend === true ? '#ffffff' : '#007bff' }} href={'/download/Business/Document/Download/' + item.key + '?filename=' + formatFileNameForUrl(item.Name)} target='_blank'>{item.Name}</a></span>
                  </span>
                </div>
              </Tooltip>
            )
          }
        </>
      } else {
        return <>
          <>{data}</>
          <div style={{ color: '#9d9d9d' }}>{record.ClientCommentUpdatedBy} {record.ClientCommentUpdatedDatetime}</div>
        </>;
      }
    }
    case 'SubJobChecklistClientDocument':
      return record.SubJobChecklistClientDocumentList?.length > 0 ? <>
        {record.SubJobChecklistClientDocumentList.map((document, i) =>
          <span key={document.key || i} className='inline-document-span' style={{
            color: '#007bff', border: '1px solid #007bff', fontSize: '0.8rem',
            padding: '0.25em 0.4em', borderRadius: '0.25rem', display: 'inline-flex',
            verticalAlign: 'baseline', lineHeight: '18px', margin: '0 6px 6px 0', cursor: 'pointer'
          }} onClick={() => download({ url: '/Business/Document/Download/' + document.key, data: null, filename: document.Name })}>{document.Name}</span>
        )}
      </> : <></>
    case 'documentList':
      if (record.DocumentList !== null && record.DocumentList.length > 0) {
        return <>
          {
            record.DocumentList.map((document, i) =>
              <div key={i}>
                <span
                  className='inline-document-span'
                  style={{
                    color: '#007bff',
                    border: '1px solid #007bff',
                    fontSize: '0.8rem',
                    padding: '0.25em 0.4em',
                    borderRadius: '0.25rem',
                    display: 'inline-flex',
                    verticalAlign: 'baseline',
                    lineHeight: '18px',
                    margin: '0 6px 6px 0',
                    cursor: 'pointer'
                  }}>
                  <span onClick={() => { download({ url: '/Business/Document/Download/' + document.key, data: null, filename: document.Name }) }}>{document.Name}</span>
                </span>
              </div>
            )
          }
        </>
      }
    case 'MailMergeTemplateName':
      return record.Docusign ? <>{data}<i className="fal fa-file-signature table-icon docusign-icon svg-img-btn"></i></> : <>{data}</>
    case 'ContactNumber':
      let contactNumber = [];
      if (!isNullOrEmpty(record.Phone)) contactNumber.push(record.Phone);
      if (!isNullOrEmpty(record.Phone) && !isNullOrEmpty(record.Mobile)) contactNumber.push(<Divider key={key + '_Divider'} type="vertical" style={{ borderLeft: '1px solid #495057' }} />);
      if (!isNullOrEmpty(record.Mobile)) {
        if (mobileReg.test(record.Mobile.replace(/\s/g, ''))) {
          contactNumber.push(
            <span key={key + '_span'} className='link'
              onClick={(e: any) => { operationalFunction({ e: e, functionName: 'sendSMS', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
            >{record.Mobile}</span>
          );
        } else {
          contactNumber.push(record.Mobile);
        }
      }
      return <>{contactNumber}</>
    case 'SiteContact':
      let mobiles = record.SiteContactMobile?.replace(/\s/g, '').split(',');
      if (mobiles !== undefined) {
        mobiles.map(mobile => {
          if (mobileReg.test(mobile)) {
            result = true;
          }
        })
        if (result) {
          return <span className='link' onClick={() => {
            sectionComponent.getSMSMessage(record);
          }}>{data}</span>
        } else {
          return <>{data}</>
        }
      } else {
        return <>{data}</>
      }
    // case 'JobContactCompany':
    //   return <><span className='link'
    //     onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showCompanyContact', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
    //   >{data}</span>
    //     {
    //       !isNullOrEmpty(data) && record.ContactTypeName === 'Applicant' && pageComponent.state.jobData.ClientUserId !== null && !isNullOrEmpty(pageComponent.state.jobData.ClientUser.Client.Note) ? <i className="fal fa-info-circle link ml-2"
    //         onMouseEnter={(e) => {
    //           pageComponent.setState({ showInfo: true, infoText: pageComponent.state.jobData.ClientUser.Client.Note, mouseEvent: e });
    //         }}
    //       /> : <></>
    //     }
    //   </>
    case 'JobContactCompany':
      if (!isNullOrEmpty(data) && record.ContactTypeName === 'Applicant' && pageComponent.state.jobData.ClientUserId !== null && !isNullOrEmpty(pageComponent.state.jobData.ClientUser.Client.Note)) {
        return <Popover content={<div dangerouslySetInnerHTML={{ __html: pageComponent.state.jobData.ClientUser.Client.Note?.replace(/\n/g, '<br />') }}></div>} trigger="hover">
          <span
            style={{ cursor: 'pointer' }}
            onClick={(e: any) => {
              operationalFunction({ e: e, functionName: 'showCompanyContact', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
              pageComponent.setState({ showInfo: false, infoText: '' });
            }}
          ><CustomBadge text={data} display="inline-block" backgroundColor="#F44336" verticalAlign="top" /></span>
        </Popover>
      } else {
        return <><span className='link'
          onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showCompanyContact', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
        >{data}</span>
        </>
      }
    case 'JobLabel':
    case 'JobTypeLabel':
      return <>{
        record[uie.source].map((item, i) => <span key={i} className="job-label"><CustomBadge text={item.Name} color={item.FontColor} backgroundColor={item.BackgroundColor} verticalAlign="top" /></span>)
      }</>
    case 'ClientLabel':
    case 'ClientJobColorOption':
      switch (sectionComponent.state.searchParameter.ClientShowType) {
        case '0':
          return <>{
            record[uie.source].filter(item => item.ModuleId === record.ModuleId).map((item, i) => <span key={i} className="job-label"><CustomBadge text={item.Name} backgroundColor={item.BackgroundColor} verticalAlign="top" /></span>)
          }</>
        case '1':
          return <>{
            record[uie.source].map((item, i) => <span key={i} className="job-label"><CustomBadge text={item.Name} backgroundColor={item.BackgroundColor} verticalAlign="top" /></span>)
          }</>
      }
    case 'ClientCategoryLabel':
      return <>{
        record[uie.source]?.map((item, i) => <span key={i} className="job-label"><CustomBadge text={item.Name} backgroundColor={item.BackgroundColor} verticalAlign="top" /></span>)
      }</>
    case 'JobNoteContent':
      let clientPortalHtml = '';
      const jobNoteContent = record.showFullContent ? data : truncateString(removeHtmlTags(data), 200, '...');
      if (record.ClientPortal) {
        return <><CustomBadge text={<span style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: jobNoteContent.replace(/\n/g, '<br />') }} />} display="inline-block" backgroundColor="#C8B800" verticalAlign="top" /><span style={{ display: 'inline-block', verticalAlign: 'bottom' }} dangerouslySetInnerHTML={{ __html: clientPortalHtml }} ></span></>
      } else if (record.CreatedByType === 1) {
        return <><CustomBadge text={<span style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: jobNoteContent.replace(/\n/g, '<br />') }} />} display="inline-block" backgroundColor="#00A622" verticalAlign="top" /><span style={{ display: 'inline-block', verticalAlign: 'bottom' }} dangerouslySetInnerHTML={{ __html: clientPortalHtml }} ></span></>
      } else if (record.Popup) {
        return <><CustomBadge text={<span style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: jobNoteContent.replace(/\n/g, '<br />') }} />} display="inline-block" backgroundColor="#FF0000" verticalAlign="top" /><span style={{ display: 'inline-block', verticalAlign: 'bottom' }} dangerouslySetInnerHTML={{ __html: clientPortalHtml }} ></span></>
      } else {
        return <><span style={{ display: 'inline-block' }} dangerouslySetInnerHTML={{ __html: jobNoteContent.replace(/\n/g, '<br />') }} /><span style={{ display: 'inline-block', verticalAlign: 'bottom' }} dangerouslySetInnerHTML={{ __html: clientPortalHtml }} ></span></>
      }
    case 'truncate':
      if (record.showTruncate === true) {
        // return <><div style={{cursor: 'pointer'}}  onClick={(e: any) => {
        //   operationalFunction({ e: e, functionName: 'showTruncate', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
        // }}>{data}</div></>  //点击后可进行缩放展示
        return <><div>{data}</div></>
      } else {
        // record.showTruncate = false //点击后可进行缩放展示
        return <><span style={{ display: 'inline-block', cursor: 'pointer' }}
          onClick={(e: any) => {
            operationalFunction({ e: e, functionName: 'showTruncate', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent });
          }}>{truncateString(removeHtmlTags(data), 200, '...')}</span></>
      }
    case 'LandAttribute':
      return <>
        {record.PptTermite ? <img className="svg-img-btn mr-2" title="Termite" src={'assets/images/icon/termite.svg'} /> : <></>}
        {record.PptFloodProne ? <img className="svg-img-btn mr-2" title="Flood Prone" src={'assets/images/icon/flood.svg'} /> : <></>}
        {record.PptBushfireProne ? <img className="svg-img-btn mr-2" title="Bushfire Prone" src={'assets/images/icon/fire.svg'} /> : <></>}
        {record.PptDesignatedLand ? <img className="svg-img-btn mr-2" title="Designated Land" src={'assets/images/icon/designated_land.svg'} /> : <></>}
        {record.PptAlpineArea ? <img className="svg-img-btn mr-2" title="Alpine Area" src={'assets/images/icon/snowflake.svg'} /> : <></>}
        {record.PptDeclaredRoad ? <img className="svg-img-btn mr-2" title="Declared Road" src={'assets/images/icon/declared_road.svg'} /> : <></>}
        {record.PptSolarHotWater ? <img className="svg-img-btn mr-2" title="Solar Hot Water" src={'assets/images/icon/solar-panel.svg'} /> : <></>}
        {record.PptRainwaterTank ? <img className="svg-img-btn mr-2" title="Rainwater Tank" src={'assets/images/icon/water-tank.svg'} /> : <></>}
        {record.PptExistingEasement ? <img className="svg-img-btn mr-2" title="Existing Easement" src={'assets/images/icon/easements.svg'} /> : <></>}
        {record.LivableHousingDesign ? <img className="svg-img-btn mr-2" style={{ marginTop: '2px' }} title="Livable Housing Design" src={'assets/images/icon/livable_housing.svg'} /> : <></>}
      </>
    case 'HideOnMailMerge':
      return record.HideOnMailMerge === true ? <span style={{ color: '#a7a7a7' }}>{data}</span> : <>{data}</>
    case 'LgaInfo':
      return !isNullOrEmpty(data) ? <>
        <span className='link'
          onClick={(e: any) => { operationalFunction({ e: e, functionName: 'sendEmail', sectionName: sectionComponent.props.sectionName, parameter: record.Lga, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
        >{data}</span>
        <i className="fal fa-info-circle link ml-2"
          onMouseEnter={(e) => {
            pageComponent.setState({ showLgaInfo: true, PrivateOrPlanning: 'Private', mouseEvent: e });
          }}
        />
      </> : <></>
    case 'LgaInfoPlanningNote':
      return !isNullOrEmpty(data) ? <>
        <span className='link'
          onClick={(e: any) => { operationalFunction({ e: e, functionName: 'sendEmail', sectionName: sectionComponent.props.sectionName, parameter: record.Lga, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
        >{data}</span>
        <i className="fal fa-info-circle link ml-2"
          onMouseEnter={(e) => {
            pageComponent.setState({ showLgaInfo: true, PrivateOrPlanning: 'Planning', mouseEvent: e });
          }}
        />
      </> : <></>
    case 'BookingRequest':
      return <span dangerouslySetInnerHTML={{ __html: data }} />
    case 'ChargedCheckbox':
      return <Checkbox className="charged-checkbox" checked={!!data} onChange={async e => {
        const charged = e.target.checked;
        const table = sectionComponent?.child?.current;
        const previousCharged = record.Charged;
        // 先更新当前行，接口失败时再恢复，避免复选框停留在旧值
        const updateRow = value => {
          if (table?.state?.data) {
            table.setState(state => ({
              data: state.data.map(group => ({
                ...group,
                SubJobList: group.SubJobList?.map(item => item.key === record.key ? { ...item, Charged: value } : item)
              }))
            }));
          } else {
            record.Charged = value;
          }
        };
        updateRow(charged);
        try {
          const response = await httpClient.get(`Business/SubJob/${record.key}`);
          const updated = { ...response.data.data, Charged: charged };
          const saveResponse = await httpClient.put('Business/SubJob', updated);
          if (saveResponse.data?.code !== 0) throw new Error('Update failed');
        } catch (error) {
          updateRow(previousCharged);
          message.error('Unable to update charged status.');
        }
      }} />
    case 'SwitchInspector':
      return <span style={{ cursor: 'pointer', color: '#007bff' }}
        onClick={(e: any) => {
          console.log(record, e);
          let showAssignInspectorModal = pageComponent.state.showAssignInspectorModal;
          pageComponent.setState({ event: e, showAssignInspectorModal: !showAssignInspectorModal, Key: record.key });
          e.preventDefault();
          return false;
        }}
      >{data}</span>;
    case 'ByteImage':
      return <Image
        className={uie.className}
        src={'data:png;base64, ' + data}
        fallback={config.component.Image.fallback}
      />
    case 'ByteImageSvg':
      return <Image
        className={uie.className}
        src={'data:image/svg+xml;base64, ' + data}
        fallback={config.component.Image.fallback}
      />
    case 'StageInfo':
      return data !== null ? <span style={{ cursor: 'pointer' }}
        onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showStageInfo', sectionName: 'Job', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
      ><CustomBadge text={data} /></span> : <></>;
    case 'LinkID':
      return data !== null ? <span style={{ cursor: 'pointer' }}
        onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showLinkInfo', sectionName: 'Job', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
      ><CustomBadge backgroundColor="#FFC107" text={data + (record.LinkIDCount != null ? ' (' + record.LinkIDCount + ')' : '')} /></span> : <></>;
    case 'SubJobReport':
      return <>
        {
          record.SubJobReportList?.map((item, i) =>
            <div key={i}>
              <span
                className='inline-document-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span>
                  <span onClick={() => { download({ url: '/Business/Document/Download/' + item.key + '?filename=' + formatFileNameForUrl(item.Name), data: null, filename: item.Name }) }}>{item.Name}</span>
                </span>
                <span style={{ margin: '0 4px 0 4px' }} >|</span>
                <Popconfirm
                  placement="bottom"
                  title={config.component.defaultDeleteConfirmText}
                  onConfirm={() => { operationalFunction({ functionName: 'deleteSubJobReport', sectionName: 'SubJob', parameter: { SubJobId: record.SubJobId, DocumentId: item.DocumentId }, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                >
                  <DeleteOutlined style={{ fontSize: '18px' }} />
                </Popconfirm>
              </span>
            </div>
          )
        }
      </>
    case 'serviceRequestFileList':
      // EPI Staff 端 Service Request 的 Files 列：仿 SubJobReport 蓝色 outlined chip
      // 含改名和删除按钮；改名成功后刷新列表显示最新名称。
      // data 即 record.DocumentList，每条含 ServiceRequestDocumentId + DocumentId + DocumentName
      return <>
        {(data || []).map((item) =>
          <ServiceRequestDocumentFile
            key={item.ServiceRequestDocumentId}
            item={item}
            sectionComponent={sectionComponent}
            pageComponent={pageComponent}
          />
        )}
      </>
    case 'serviceRequestLatestNote':
      return data != null
        ? <div>
          <div>{moment(data.DateCreated, 'DD/MM/YYYY HH:mm').format('DD/MM/YYYY HH:mm')}</div>
          <div>{data.Content}</div>
        </div>
        : <></>
    case 'ComplaintDocument':
      return <>
        {
          record.ComplaintDocumentList?.map((item, i) =>
            <div key={i}>
              <span
                className='inline-document-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span><a style={{ color: '#007bff' }} href={'/download/Business/Document/Download/' + item.key + '?filename=' + formatFileNameForUrl(item.Name)} target='_blank'>{item.Name}</a></span>
                <span style={{ margin: '0 4px 0 4px' }} >|</span>
                <Popconfirm
                  placement="bottom"
                  title={config.component.defaultDeleteConfirmText}
                  onConfirm={() => { operationalFunction({ functionName: 'deleteComplaintDocument', sectionName: 'Complaint', parameter: { ComplaintId: record.ComplaintId, DocumentId: item.DocumentId }, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                >
                  <DeleteOutlined style={{ fontSize: '18px' }} />
                </Popconfirm>
              </span>
            </div>
          )
        }
      </>
    case 'SubJobLabel':
      return <>
        {
          record.SubJobLabelOptionList?.map((item, i) =>
            <span key={i} className="job-label"><CustomBadge text={item.Name} color={item.FontColor} backgroundColor={item.BackgroundColor} verticalAlign="top" /></span>
          )
        }
      </>
    case 'reportLink':
      return <span className='link'
        onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showReportDiv', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
      >{data}</span>
    case 'quoteCheckAcceptDate':
      if (record['AcceptDate'] !== null) {
        <CustomBadge text={truncateString(data, 10)} backgroundColor="#28A745" />
      } else {
        return truncateString(data, 10);
      }
    case 'OnlinePaymentDate':
      return data !== null && data !== undefined ? <CustomBadge text={truncateString(data, 10)} backgroundColor="#28A745" /> : <></>
    case 'WorkflowStatusFree':
      return data === true ? <CustomBadge text="Free" backgroundColor="#00A622" /> : <></>
    case 'WorkflowStatusMeta':
      return <>
        {record.InitStatus ? <span className="custom-badge-span"><CustomBadge text="Starting" backgroundColor="#03A9F4" /></span> : <></>}
        {record.Free ? <span className="custom-badge-span"><CustomBadge text="Free" backgroundColor="#00A622" /></span> : <></>}
        {record.Closed ? <span className="custom-badge-span"><CustomBadge text="Closed" backgroundColor="#000000" /></span> : <></>}
        {record.ClientNewJob ? <span className="custom-badge-span"><CustomBadge text="Client New Job" backgroundColor="#F5A623" /></span> : <></>}
        {record.Rfi ? <span className="custom-badge-span"><CustomBadge text="RFI" backgroundColor="#F5A623" /></span> : <></>}
        {record.Inspection ? <span className="custom-badge-span"><CustomBadge text="Inspection" backgroundColor="#F5A623" /></span> : <></>}
        {record.RequiredCertificate ? <span className="custom-badge-span"><CustomBadge text="Required Certificate" backgroundColor="#F5A623" /></span> : <></>}
        {record.PostConstruction ? <span className="custom-badge-span"><CustomBadge text="Post Construction" backgroundColor="#F5A623" /></span> : <></>}
      </>
    case 'BusinessNo':
      return <Link target="_blank" to={{ pathname: 'https://abr.business.gov.au/ABN/View?id=' + data?.replace(/\s+/g, '') }}>{data}</Link>
    case 'Availability':
      var date = new Date(new Date().toLocaleDateString())
      var tagShow = false
      var titleShow = null
      if (record.CompanyUserNotAvailabilityList.length > 0) {
        record.CompanyUserNotAvailabilityList.forEach(element => {
          var startDate = new Date(moment(moment(element.StartDate, config.format.date)))
          var endDate = new Date(moment(moment(element.EndDate, config.format.date)))
          if (date >= startDate && date <= endDate) {
            tagShow = true
            titleShow = 'NA till' + truncateString(element.EndDate, 10)
          }
        })
      }
      return data !== null ? <span style={{ cursor: 'pointer' }}
        onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showAvailability', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
      >{tagShow ? <CustomBadge text={titleShow} backgroundColor={'#F44336'} /> : <CustomBadge text={'Available'} backgroundColor={'#00A622'} />}</span> : <></>;
    // case 'AppIntegrationTypeName':
    //   console.log(record.flip);
    //   return data === 'Xero' ? <><span>{data}</span>
    //     <div
    //       style={{ float: 'right', width: '30px' }}
    //     >
    //       <img
    //         className={'svg-img-btn ml-2 ' + (record.flip == true ? 'flip' : '')}
    //         title="Sync Accounting Code"
    //         src={'assets/images/icon/sync.svg'}
    //         onClick={(e: any) => { operationalFunction({ e: e, functionName: 'reloadAccountCode', sectionName: 'AppIntegration', parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }} /></div></>
    //     : data;
    case 'AppIntegrationTypeName':
      return data === 'Xero' || data === 'MYOB' ?
        <><span>{data}</span>
          <span className='document-name-span'><div
            style={{ float: 'right', width: '30px', cursor: 'pointer' }}
          >
            <Button
              icon={<i className="icon icon-menu ant-menu-item-icon"></i>}
              type='link'
              className='image-button-div-cion document-name-icon'
              onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showAppIntegrationMenu', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
            >
            </Button>
          </div ></span>
        </> : data;
    case 'jobLink':
      return <>
        {
          record.JobLinkList?.map((jobLink, i) =>
            <Tag
              closable
              style={{
                fontSize: '0.8rem',
                borderRadius: '0.25rem',
                color: '#ffffff',
              }}
              color={jobLink.Type == 1 ? '#00A143' : '#e88b02'}
              onClose={(e) => {
                { operationalFunction({ e: e, functionName: 'deleteJobLink', sectionName: 'Job', parameter: jobLink, sectionComponent: sectionComponent, pageComponent: pageComponent }) }
              }}
              key={i}>
              <span style={{ cursor: 'pointer' }} onClick={() => window.open('/job/detail/' + jobLink.LinkedJobId, '_blank')}>{jobLink.JobNum}</span>
              <Divider type="vertical" style={{ borderLeft: '1px solid #ffffff', marginRight: '4px' }} />
            </Tag>
          )
        }
      </>;
    case 'ClientDueDate':
      return <><span> {record.ClientDueDate !== null ? record.ClientDueDate : ''} {record.ClientDueDateNote}</span></>;
    case 'JobAssessmentChecklistDetail':
      return !isNullOrEmpty(data) ? <>
        <img className="svg-img-btn" src={'assets/images/icon/note.svg'} onMouseEnter={(e) => {
          pageComponent.setState({ showJobAssessmentChecklistDetail: true, mouseEvent: e, Detail: data });
        }} />
      </> : <></>
    case 'CopyJobAddress':
      return <><span>{data}</span>
        <div
          className='hide-icon-span ml-2 '
          style={{ width: '30px', cursor: 'pointer', display: 'inline-block' }}
        >
          <Tooltip mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title="Copy property address without Lot">
            <Button
              icon={<i className="fal fa-copy"></i>}
              type='link'
              className='hide-icon-icon'
              onClick={async (e: any) => {
                message.info('Copied to clipboard!');
                await navigator.clipboard.writeText(record.AddressWithoutLotNo);
              }}
            >
            </Button>
          </Tooltip >
        </div>
      </>
    case 'avatar':
      return <>
        <span>
          <CompanyUserAvatar
            record={record}
            fallback={getAvatarName(record.FirstName + ' ' + record.LastName)}
            onClick={(avatarImgUrl, avatarFile) => {
              pageComponent.setState({
                showAvatarDiv: true,
                avatarDataList: record,
                avatarImgUrl,
                avatarFileList: avatarFile ? [avatarFile] : []
              });
            }}
          />
        </span>
        <span style={{ marginLeft: '5px' }}>{record.BackgroundColor !== null ? <CustomBadge text={data} backgroundColor={record.BackgroundColor} /> : data}</span>
      </>
    case 'ClickSpan':
      return <><span
        className="gx-link"
        onClick={(e) => {
          operationalFunction({ e: e, functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
        }}
      >
        {data}
      </span></>
    case 'taskTemplateClickSpan':
      return <><span
        className="gx-link"
        onClick={(e) => {
          operationalFunction({ e: e, functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
        }}
      >
        <>{record.Name}</>
      </span>{record.Notify == true ? <img style={{ width: 16, verticalAlign: 'middle', marginLeft: 10, marginBottom: 1 }} src={'assets/images/icon/email_solid.svg'} /> : <></>}</>
    case 'BySubContacts':
      const { CompanySubContactList = [] } = record || {};
      const renderSubContact = (item) => {
        const fullName = `${item.FirstName} ${item.LastName}`.trim();
        const noteText = item.Notes ? ` (${item.Notes})` : '';
        const EmailAndMobile = [];
        if (item.Email) EmailAndMobile.push(item.Email);
        if (item.Mobile) EmailAndMobile.push(item.Mobile);
        const emailAndMobile = EmailAndMobile.join(' | ');
        return `${fullName}${noteText}${emailAndMobile ? `: ${emailAndMobile}` : ''}`;
      };
      const subContactList = CompanySubContactList
        .map(item => renderSubContact(item))
        .filter(Boolean);
      return (
        <>
          {subContactList.length > 0
            ? subContactList.map((text, index) => (
              <span key={index}>
                {text}
                {index !== subContactList.length - 1 && (
                  <><br /></>
                )}
              </span>
            ))
            : <span></span>}
        </>
      );
    case 'SubJobResultOptionActionPackTrigger':
      return <>
        {
          record.SubJobResultOptionActionPackTriggerList?.map((subJobResultOptionActionPackTrigger, i) =>
            <div key={i}>{subJobResultOptionActionPackTrigger.JobType.Name}: {subJobResultOptionActionPackTrigger.ActionPack.Name}</div>
          )
        }
      </>
    case 'JobChecklistStaffUploaded': {
      const pictureFormat = ['jpg', 'jpeg', 'png', 'bmp'];
      const getFileFormat = (name) => {
        if (!name) return '';
        const clean = name.split('?')[0];
        return clean.split('.').pop().toLowerCase();
      };
      return (
        <>
          {record.JobChecklistDocumentList?.length > 0 &&
            record.JobChecklistDocumentList
              .filter((document) => {
                const ext = getFileFormat(document.Name);
                return ext && !pictureFormat.includes(ext);
              })
              .map((document, i) => (
                <div key={i}>
                  <span
                    className="inline-document-span"
                    style={{
                      color: '#007bff',
                      border: '1px solid #007bff',
                      fontSize: '0.8rem',
                      padding: '0.25em 0.4em',
                      borderRadius: '0.25rem',
                      display: 'inline-flex',
                      verticalAlign: 'baseline',
                      lineHeight: '18px',
                      margin: '0 6px 6px 0',
                      cursor: 'pointer'
                    }}
                  >
                    <span><a style={{ color: '#007bff' }} href={'/download/Business/Document/Download/' + document.DocumentId + '?filename=' + formatFileNameForUrl(document.Name)} target="_blank" rel="noreferrer" >
                      {document.Name}
                    </a>
                    </span>
                    <span style={{ margin: '0 4px' }}>|</span>

                    <Popconfirm
                      placement="bottom"
                      title={config.component.defaultDeleteConfirmText}
                      onConfirm={() => {
                        operationalFunction({
                          functionName: 'deleteJobChecklistImage',
                          sectionName: sectionComponent.props.sectionName,
                          parameter: document,
                          sectionComponent,
                          pageComponent,
                          uie
                        });
                      }}
                    >
                      <DeleteOutlined style={{ fontSize: '18px' }} />
                    </Popconfirm>
                  </span>
                </div>
              ))}
        </>
      );
    }
    case 'copyProject':
      return <>
        <span
          style={{ cursor: 'pointer' }}
          onClick={async (e: any) => {
            message.info('Copied to clipboard!');
            await navigator.clipboard.writeText(record.JobNum + ' - ' + record.Name);
          }}
          title="Copy">{data}</span>
      </>
    default:
      if (!isNullOrEmpty(uie.unit)) {
        if (data !== null && data !== '') {
          return <>{data}{uie.unit}</>
        } else {
          return <></>
        }
      } else {
        return <>{data}</>
      }
  }
}

export function getByteImageSrc(image) {
  let type = '';
  switch (getExtension(image.Name)) {
    case '.jpg':
    case '.jpeg':
      type = 'image/jpeg';
      break;
    case '.png':
      type = 'image/png';
      break;
    case '.bmp':
      type = 'image/bmp';
      break;
    case '.heic':
      type = 'image/heic';
      break;
    case '.heif':
      type = 'image/heif';
      break;
    default:
      type = 'image/jpeg';
      break;
  }
  return 'data:' + type + ';base64, ' + image.Data;
}

/**
 * 异步文件下载功能
 * @param url 文件url地址
 * @param filename 导出的文件名
 */
export async function download({ method = 'get', url, sectionName, data, filename, save, messageText, errorMessageText = 'Error!' }) {
  message.info(messageText === undefined ? 'Downloading - ' + filename : messageText);

  var extension = filename.split('.').pop().toLowerCase();
  let isOpenFileInBrowser = false;
  switch (extension) {
    case 'pdf':
    case 'jpeg':
    case 'jpg':
    case 'png':
      if (save !== true) {
        isOpenFileInBrowser = true;
      }
      break;
  }

  console.log(isOpenFileInBrowser);
  if (isOpenFileInBrowser) {
    //新窗口打开pdf或者图片
    if (url.indexOf('?') >= 0) {
      url += '&filename=' + formatFileNameForUrl(filename);
    } else {
      url += '?filename=' + formatFileNameForUrl(filename);
    }
    var newWindow = window.open('/download' + url, '_blank');
  } else {
    //强制下载
    return httpClient({
      method: method,
      url: url,
      data: data,
      responseType: 'blob'
    })
      .then(({ data }) => {
        if (data != null) {
          let blobUrl = URL.createObjectURL(data);
          saveFile(blobUrl, filename);
        } else {
          message.warning(errorMessageText);
        }
      })
      .catch(function (error) {
        message.warning(errorMessageText);
      });
  }
}

function openFileInBrowser(blobUrl, filename) {
  var newWindow = window.open(blobUrl);
  setTimeout(() => {
    if (newWindow !== null) {
      newWindow.document.title = filename;
    }
  }, 200);
}

export function isIOS() {
  // 判断是否为苹果移动设备
  return (
    (
      navigator.userAgent.indexOf('AppleWebKit') !== -1 || // 判断是否为AppleWebKit
      navigator.userAgent.indexOf('iPhone') !== -1 || // 判断是否为iPhone
      navigator.userAgent.indexOf('iPad') !== -1 || // 判断是否为iPad
      navigator.userAgent.indexOf('iPod') !== -1 // 判断是否为iPod
    ) && navigator.userAgent.indexOf('Android') === -1 && navigator.userAgent.indexOf('Windows') === -1
  );
}

export function isAndroid() {
  // 判断是否为Android设备
  return navigator.userAgent.indexOf('Android') !== -1;
}

export function isMobile() {
  // 判断是否为移动设备
  return (
    typeof window.orientation !== "undefined" || // 判断是否存在window.orientation属性，此属性在移动设备上一般存在
    navigator.userAgent.indexOf('IEMobile') !== -1 || // 判断是否为Windows Phone
    navigator.userAgent.indexOf('iPhone') !== -1 || // 判断是否为iPhone
    navigator.userAgent.indexOf('iPad') !== -1 || // 判断是否为iPad
    navigator.userAgent.indexOf('iPod') !== -1 || // 判断是否为iPod
    navigator.userAgent.indexOf('Android') !== -1 || // 判断是否为Android手机
    navigator.userAgent.indexOf('BlackBerry') !== -1 || // 判断是否为BlackBerry
    navigator.userAgent.indexOf('Opera Mini') !== -1 // 判断是否为Opera Mini浏览器
  );
}

export function saveFile(blobUrl, filename) {
  let link = document.createElement('a');
  link.download = filename;
  link.style.display = 'none';
  link.href = blobUrl;
  // 触发点击
  document.body.appendChild(link);
  link.click()
  // 移除
  document.body.removeChild(link);
}

// 构造内置 pdf.js 查看器 URL；把原始文件名作为 #片段嵌入 file 参数，
// 使 pdf.js 查看器“下载”按钮能取到正确文件名（否则只会得到 blob 的 GUID）。
// 注意：必须对 blobUrl + '#' + filename 整体 encodeURIComponent，否则 #片段会被当成 viewer.html 自身的 hash。
export function getPdfViewerUrl(blobUrl, filename) {
  const fileParam = filename ? blobUrl + '#' + filename : blobUrl;
  return '/assets/pdfjs/web/viewer.html?file=' + encodeURIComponent(fileParam);
}

//base64转成blob
export function base64ToBlob(base64, mimeType = '') {
  const sliceSize = 1024;
  const byteChars = window.atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
    const slice = byteChars.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: mimeType });
  return blob;
}

let _sessionExpiredShown = false;
let _networkErrorShown = false;

function showSessionExpired(error) {
  if (_sessionExpiredShown) return;
  _sessionExpiredShown = true;
  const reasons = {
    token_expired: 'The login token has expired.',
    security_stamp_changed: 'This login was revoked. A password change or sign-out on all devices may have occurred.',
    account_inactive_or_missing: 'This account is disabled or no longer available.',
    ip_not_allowed: 'This network is not allowed for this account.',
    invalid_claims: 'The login token is missing account information.',
    invalid_token: 'The login token could not be verified.',
    authentication_error: 'Login verification failed.',
    missing_authorization: 'The request did not include a login token.'
  };
  const reason = reasons[error?.sessionReason] || 'The API returned 401; the exact cause is not available.';
  const request = [error?.method?.toUpperCase(), error?.requestPath].filter(Boolean).join(' ') || 'Unknown API';
  const occurredAt = moment();
  Modal.error({
    title: 'Session Expired',
    content: <div>
      <div style={{ marginBottom: 12 }}>Click OK to login again.</div>
      <div style={{ color: '#767676' }}>
        <div>{reason}</div>
        <div>API: {request}</div>
        <div>Response: {error?.status === 401 ? 'HTTP 401' : 'API code 401'}</div>
        <div>Browser time: {occurredAt.format('YYYY-MM-DD HH:mm:ss Z')}</div>
        <div>UTC: {occurredAt.clone().utc().format('YYYY-MM-DD HH:mm:ss')}</div>
      </div>
    </div>,
    onOk() {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  });
}

//处理返回数据的错误信息
export function handleError(error, pageComponent) {
  console.log(error);
  console.log(pageComponent);
  switch (error.status) {
    case 401:
      showSessionExpired(error);
      break;
    default:
      switch (error.code) {
        case 'ERR_NETWORK':
          if (!_networkErrorShown) {
            _networkErrorShown = true;
            message.error('Network Error.');
            setTimeout(() => { _networkErrorShown = false; }, 5000);
          }
          break;
        default:
          let errorMessage = error.stack.replace(/\n/g, '<br />');
          if (pageComponent?.props?.history !== undefined) {
            pageComponent.props.history.push('/error?type=UI&message=' + errorMessage);
          } else {
            window.location.href = '/error?type=UI&message=' + errorMessage;
          }
      }
      break;
  }

}

//处理返回数据的错误信息
export function handleResultError(data, pageComponent) {
  console.log(data.code);
  console.log(pageComponent);
  switch (data.code) {
    case 401:
      showSessionExpired(data);
      break;
    // case 'ERR_NETWORK':
    //   message.error('Axios Network Error.');
    //   break;
    default:
      let errorMessage = data.message.replace(/\n/g, '<br />');
      if (pageComponent?.props?.history !== undefined) {
        pageComponent.props.history.push('/error?type=API&message=' + errorMessage);
      } else {
        window.location.href = '/error?type=API&message=' + errorMessage;
      }
      break;
  }
}

//获取名字首字母缩写
export function getAvatarName(name) {
  let result = '';
  let wordArray = name.split(' ');
  for (let i = 0; i < wordArray.length; i++) {
    if (wordArray[i].trim() !== '') {
      result += wordArray[i][0].toUpperCase();
    }
  }
  return result;
}

//null转空字符串
export function nullToEmpty(str) {
  return str !== null ? str : '';
}

//空字符串转null
export function emptyToNull(str) {
  return str !== '' ? str : null;
}

//判断是否为空字符串
export function isNullOrEmpty(str) {
  return str === undefined || str === null || str.trim() === '';
}

//确认弹框
export const confirm = ({ title = 'Confirm',
  icon = <ExclamationCircleOutlined />,
  content = '',
  callback = null,
  okText = 'OK',
  okType = null,
  width = null
}) => {
  Modal.confirm({
    title: title,
    icon: icon,
    content: content,
    onOk: callback,
    okText: okText,
    okType: okType,
    width: width,
  });
};

//字符串格式化时间
export function formatDate(str) {
  var tmp = str.substring(0, 10).split('/');
  return new Date(tmp[2], tmp[1] - 1, tmp[0]);
}

//增加日期天数
export function addDate(startDate, addDays) {
  return new Date(startDate.setDate(startDate.getDate() + addDays));
}

//增加日期天数 - moment
export function addDateMoment(startDate, addDays) {
  let date = new Date(Date.parse(moment(startDate).format(config.format.sqlDate).replace(/-/g, "/")));
  let newDate = addDate(date, addDays);
  return moment(newDate, config.format.date);
}

// 增加日期月份
export function addMonth(startDate, addMonths) {
  return new Date(startDate.setMonth(startDate.getMonth() + addMonths));
}

// 增加日期月份 - moment
export function addMonthMoment(startDate, addMonths) {
  let date = new Date(Date.parse(moment(startDate).format(config.format.sqlDate).replace(/-/g, "/")));
  let newDate = addMonth(date, addMonths);
  return moment(newDate, config.format.date);
}

//增加日期年份
export function addYear(startDate, addYears) {
  return new Date(startDate.setFullYear(startDate.getFullYear() + addYears));
}

//增加日期年份 - moment
export function addYearMoment(startDate, addYears) {
  let date = new Date(Date.parse(moment(startDate).format(config.format.sqlDate).replace(/-/g, "/")));
  let newDate = addYear(date, addYears);
  return moment(newDate, config.format.date);
}

//获取文件名(不包括扩展名)
export function getFileNameWithoutExtension(filename) {
  return filename.substring(0, filename.lastIndexOf("."));
}

//获取文件扩展名
export function getExtension(filename) {
  return filename.substring(filename.lastIndexOf("."), filename.length);
}

//获取以Page为查找基础的Section组件
export function getSectionComponent(pageComponent, sectionName) {
  let sectionComponent = null;
  if (pageComponent.createPage?.current != null) {
    pageComponent.createPage.current?.child.map((section, i) => {
      if (section.current?.props.sectionName === sectionName) {
        sectionComponent = section.current;
      }
    })
  }
  return sectionComponent;
}

//设置上传文件的状态并返回文件列表
export function setFileList({ fileList, file, uploadProgress, status }) {
  fileList.map((oldFile, i) => {
    if (status !== undefined) {
      if (oldFile.name === file.name && oldFile.size === file.size) {
        oldFile.percent = uploadProgress;
      }
      if (oldFile.percent === 100) {
        oldFile.status = 'done';
      } else {
        oldFile.status = 'uploading';
      }
    } else {
      oldFile.status = status;
    }
    return oldFile;
  })
}

//设置控件是否可见
export function setUieVisible(pageComponent, sectionName, type, source, show) {
  let page = pageComponent.createPage.current?.state.page;
  page.sectionList.filter(c => c.name === sectionName)[0].componentList.filter(c => c.type === type)[0].groupList.map((group, i) => {
    group.uieList.map((uie, i) => {
      if (uie.source === source) {
        uie.show = show;
      }
      return uie;
    });
    return group;
  })
  pageComponent.createPage.current?.setState({ page });
}

export function setUieTitle(pageComponent, sectionName, type, source, title) {
  let page = pageComponent.createPage.current?.state.page;
  page.sectionList.filter(c => c.name === sectionName)[0].componentList.filter(c => c.type === type)[0].groupList.map((group, i) => {
    group.uieList.map((uie, i) => {
      if (uie.source === source) {
        uie.title = title;
      }
      return uie;
    });
    return group;
  })
  pageComponent.createPage.current?.setState({ page });
}

//设置控件组是否可见
export function setGroupVisible(pageComponent, sectionName, type, groupType, show) {
  let page = pageComponent.createPage.current?.state.page;
  page.sectionList.filter(c => c.name === sectionName)[0].componentList.filter(c => c.type === type)[0].groupList.map((group, i) => {
    if (group.type === groupType) {
      group.show = show;
    }
    return group;
  })
  pageComponent.createPage.current?.setState({ page });
}

//替换Table的Uie属性, 多用于修改title
export function replaceUie({ tableComponent, componentType, uieSource, replaceParameter } = {}) {
  if (tableComponent != null) {
    let columns = tableComponent.state.columns;
    columns = columns.map((column, i) => {
      replaceParameter.map((parameter, m) => {
        if (column[parameter.targetKey] === parameter.targetValue) {
          column[parameter.replaceKey] = parameter.replaceValue;
        }
      })
      return column;
    })
    tableComponent.setState({ columns });
  }
}

//获取UTC时间字符串
export function getUTCDate() {
  let now = new Date();
  let str = now.getUTCFullYear() +
    '-' + ('0' + (now.getUTCMonth() + 1)).slice(-2) +
    '-' + ('0' + now.getUTCDate()).slice(-2) +
    ' ' + ('0' + now.getUTCHours()).slice(-2) +
    ':' + ('0' + now.getUTCMinutes()).slice(-2) +
    ':' + ('0' + now.getUTCSeconds()).slice(-2);
  return str;
}

//秒数转X时X分X秒
export function secondsToTime(inputSeconds) {
  let hours = Math.floor(inputSeconds / 3600);
  let minutes = Math.floor((inputSeconds % 3600) / 60);
  let seconds = inputSeconds % 60;
  let result = '';
  if (hours > 0) {
    result += hours + 'h ';
  }
  if (minutes > 0) {
    result += minutes + 'm ';
  }
  result += seconds + 's';
  return result;
}

//字符串每个首字母大写
export function titleCase(str) {
  var newStr = str.split(" "); for (var i = 0; i < newStr.length; i++) {
    newStr[i] = newStr[i].slice(0, 1).toUpperCase() + newStr[i].slice(1).toLowerCase();
  } return newStr.join(" ");
}

//获取文件的Base64
export function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

//去掉文件名里，影响 url 的字符
export function formatFileNameForUrl(filename) {
  return filename.replace(/["<>%#&={}|\\^~[\]`]/g, '');
}

//解压zip里的字符串数据
export function decompressData(data) {
  let binaryString = window.atob(data);
  let binaryLen = binaryString.length;
  let bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    let ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }

  let jszip = new JSZip();
  return jszip.loadAsync(bytes).then(function (zip) {
    return zip.file("data.txt").async("string").then(function (data) {
      // data should now contain your JSON string
      let jsonData = JSON.parse(data);
      // Now jsonData should be your original object
      return jsonData;
    });
  });
}

//获取page的config
export function getPageConfig(webUIConfig, areaName, module, pageName, condition) {
  let config = null;
  let areas = webUIConfig?.areaList.filter(item => item.name === areaName && item.module === module);
  if (areas?.length > 0) {
    let pages = areas[0].pageList.filter(item => item.name === pageName && (item.condition == null || item.condition.split(',').map(c => c.trim()).includes(condition.toString())));
    if (pages?.length > 0) {
      config = pages[0];
    }
  }
  return config;
}

export function formatPercent(percent) {
  const number = parseFloat(percent);
  return isNaN(number) ? 0 : Math.round(number);
}

//获取被禁止的日期
export function getDisabledDate(current, disabledDates, cutoffHours, cutoffMinutes) {
  // 确保比较的都是日期的开始部分
  const startOfCurrent = current.startOf('day');
  const todayStart = moment().startOf('day');

  // 禁用今天及今天之前的日期
  if (startOfCurrent <= todayStart) {
    return true;
  }

  // 禁用 disabledDates 数组中的日期
  if (disabledDates.some(disabledDate => current.isSame(disabledDate, 'day'))) {
    return true;
  }

  // 获取当前时间
  const now = new Date();
  const today = now.getDay(); // 周几 (0-6)
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // 禁用周六和周日
  if (current.day() === 0 || current.day() === 6) {
    return true;
  }

  // 如果当前时间是下午4点以后，禁用明天
  if ((currentHour > cutoffHours || (currentHour === cutoffHours && currentMinute >= cutoffMinutes)) && current.isSame(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1), 'day')) {
    return true;
  }

  // 如果今天是周六或周日，禁用下一个星期一
  if ((today === 0 || today === 6) && current.isSame(new Date(now.getFullYear(), now.getMonth(), now.getDate() + ((8 - today) % 7)), 'day')) {
    return true;
  }

  return false;
}

//登录时根据某些条件, 特殊处理WebUIConfig
export function formatWebUIConfig(companyUser, webUIConfig) {
  webUIConfig.areaList = webUIConfig?.areaList.map(area => {
    switch (area.name) {
      case 'Business':
        if (!isNullOrEmpty(area.module)) {
          //进入各module相关的配置
          area.pageList.map(page => {
            switch (page.name) {
              case 'JobDetail':
                //判断权限
                page.sectionList = page.sectionList.filter(section => companyUser.MaxPermissionLevel >= section.permissionLevel);
                page.sectionList.map(section => {
                  //进入JobDetail的section
                  switch (section.name) {
                    //不是Admin的用户，不能编辑删除JobNote
                    case 'JobNote':
                      section.componentList.map(component => {
                        switch (component.type) {
                          case 'View':
                            if (!(companyUser.MaxPermissionLevel >= 100)) {
                              component.rowButtonList = [{
                                buttonList: [{
                                  actionType: null,
                                  className: null,
                                  confirmText: null,
                                  danger: false,
                                  href: null,
                                  icon: "writing.svg",
                                  onClick: "editBySection",
                                  text: null,
                                  title: "Edit Note",
                                  type: "link",
                                }]
                              }];
                            }
                            break;
                          case 'Edit':
                            if (!(companyUser.MaxPermissionLevel > 40)) {
                              component.groupList.map((group, j) => {
                                group.uieList.map((uie, k) => {
                                  if (uie.source == 'Content') {
                                    uie.readonly = true;
                                  }
                                })
                              })
                            }
                            break;
                        }
                        return component;
                      });
                      break;

                    //权限 < 100 的用户隐藏 RestrictedUserIds 编辑字段
                    case 'Job':
                      if (companyUser.MaxPermissionLevel < 100) {
                        section.componentList.map(component => {
                          if (component.type === 'Edit') {
                            component.groupList.map(group => {
                              group.uieList = group.uieList.filter(uie => uie.source !== 'RestrictedUserIds');
                              return group;
                            });
                          }
                          return component;
                        });
                      }
                      break;

                    //没有配置CloudDocLocalPath的话, 隐藏云编辑同步按钮
                    case 'Document':
                      section.componentList.map(component => {
                        switch (component.type) {
                          case 'View':
                            if (isNullOrEmpty(companyUser.CloudDocLocalPath)) {
                              if (component.rowButtonList[0] !== null && component.rowButtonList[0] !== undefined) {
                                component.rowButtonList[0].buttonList = component.rowButtonList[0]?.buttonList?.filter(item => item.actionType !== 'CloudEdit');
                              }
                            }
                            break;
                        }
                        return component;
                      });
                      break;

                    //权限 < 98 的用户隐藏 DisableOverDueInvoiceNotifition 编辑字段
                    case 'Invoice':
                      if (companyUser.MaxPermissionLevel < 98) {
                        section.componentList.map(component => {
                          component.groupList.map(group => {
                            group.uieList = group.uieList.filter(uie => uie.source !== 'DisableOverDueInvoiceNotifition');
                            return group;
                          });
                          return component;
                        });
                      }
                      break;
                  }
                  return section;
                });
                break;
            }
            return page;
          });
        } else {
          area.pageList.map(page => {
            switch (page.name) {
              case 'Report':
                page.sectionList.map(section => {
                  //Report
                  switch (section.name) {
                    //不是Admin的用户，删除所有按钮
                    case 'Report':
                      section.componentList.map(component => {
                        switch (component.type) {
                          case 'View':
                            if (!(companyUser.MaxPermissionLevel >= 100)) {
                              component.topButtonList = [];
                              component.rowButtonList = [];
                            }
                            break;
                        }
                        return component;
                      });
                      break;
                  }
                  return section;
                });
                break;
            }
          })
        }
        break;
      case 'Admin':
        area.pageList.map(page => {
          switch (page.name) {
            case 'CompanyContact':
              page.sectionList.map(section => {
                //进入CompanyContact的section
                switch (section.name) {
                  //不是Admin的用户，看不到导出excel按钮
                  case 'CompanyContact':
                    section.componentList.map(component => {
                      switch (component.type) {
                        case 'View':
                          if (!(companyUser.MaxPermissionLevel >= 100)) {
                            if (component.topButtonList[0] !== null && component.topButtonList[0] !== undefined) {
                              component.topButtonList[0].buttonList = component.topButtonList[0]?.buttonList?.filter(item => item.actionType !== 'Excel');
                            }
                          }
                          break;
                      }
                      return component;
                    });
                    break;
                }
                return section;
              });
              break;
            case 'CompanyEmailTemplate':
              page.sectionList.map(section => {
                //进入CompanyEmailTemplate的section
                switch (section.name) {
                  //不是Admin的用户，只能看到Favorite按钮
                  case 'CompanyEmailTemplate':
                    section.componentList.map(component => {
                      switch (component.type) {
                        case 'View':
                          if (!(companyUser.MaxPermissionLevel >= section.permissionLevel)) {
                            if (component.rowButtonList[0] !== null && component.rowButtonList[0] !== undefined) {
                              component.rowButtonList[0].buttonList = component.rowButtonList[0]?.buttonList?.filter(item => item.actionType === 'UpdateFavorite');
                            }
                          }
                          break;
                      }
                      return component;
                    });
                    break;
                }
                return section;
              });
              break;
            case 'ActionPack':
              page.sectionList.map(section => {
                //进入ActionPack的section
                switch (section.name) {
                  //不是Admin的用户，只能看到Favorite按钮
                  case 'ActionPack':
                    section.componentList.map(component => {
                      switch (component.type) {
                        case 'View':
                          if (!(companyUser.MaxPermissionLevel >= 100)) {
                            if (component.rowButtonList[0] !== null && component.rowButtonList[0] !== undefined) {
                              component.rowButtonList[0].buttonList = component.rowButtonList[0]?.buttonList?.filter(item => item.actionType === 'UpdateFavorite');
                            }
                            if (component.groupList[0] !== null && component.groupList[0] !== undefined) {
                              component.groupList[0].uieList.map(uie => {
                                if (uie.type == 'pageLink') {
                                  uie.type = null;
                                }
                              })
                            }
                          }
                          break;
                      }
                      return component;
                    });
                    break;
                }
                return section;
              });
              break;
            case 'NoteTemplate':
              page.sectionList.map(section => {
                //进入NoteTemplate的section
                switch (section.name) {
                  //不是Admin的用户，只能看到Favorite按钮
                  case 'NoteTemplate':
                    section.componentList.map(component => {
                      switch (component.type) {
                        case 'View':
                          if (!(companyUser.MaxPermissionLevel >= 100)) {
                            if (component.rowButtonList[0] !== null && component.rowButtonList[0] !== undefined) {
                              component.rowButtonList[0].buttonList = component.rowButtonList[0]?.buttonList?.filter(item => item.actionType === 'UpdateFavorite');
                            }
                          }
                          break;
                      }
                      return component;
                    });
                    break;
                }
                return section;
              });
              break;
            case 'TaskTemplate':
              page.sectionList.map(section => {
                //进入TaskTemplate的section
                switch (section.name) {
                  //不是Admin的用户，只能看到Favorite按钮
                  case 'TaskTemplate':
                    section.componentList.map(component => {
                      switch (component.type) {
                        case 'View':
                          if (!(companyUser.MaxPermissionLevel >= 100)) {
                            if (component.rowButtonList[0] !== null && component.rowButtonList[0] !== undefined) {
                              component.rowButtonList[0].buttonList = component.rowButtonList[0]?.buttonList?.filter(item => item.actionType === 'UpdateFavorite');
                            }
                          }
                          break;
                      }
                      return component;
                    });
                    break;
                }
                return section;
              });
              break;
            case 'CompanyUser':
              page.sectionList.map(section => {
                //进入TaskTemplate的section
                switch (section.name) {
                  //不是Admin的用户，只能看到Favorite按钮
                  case 'CompanyUser':
                    section.componentList.map(component => {
                      switch (component.type) {
                        case 'View':
                          if (companyUser.MaxPermissionLevel == 44) {
                            component.rowButtonList = [{
                              buttonList: []
                            }];
                            component.topButtonList = [{
                              buttonList: []
                            }];
                          }
                          break;
                      }
                      return component;
                    });
                    break;
                }
                return section;
              });

              break;

          }
          return page;
        });
        break;
    }
    return area;
  });
  return webUIConfig;
}

//去掉所有html标记
function removeHtmlTags(str) {
  return str?.replace(/<[^>]*>/g, ' ');
}

//\n分割行的字符串替换成<p>标签包裹
export function convertToParagraphs(text) {
  if (text === null || text === undefined) {
    return '';
  }

  // 将文本按换行符分割成数组
  const lines = text.split('\n');

  // 将每行文本用 <p> 标签包裹，然后合并成一个字符串
  const wrappedText = lines.map(line => `<p>${line}</p>`).join('');

  return wrappedText;
}

export function getLastDayOfOneMonthsLater(date) {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-11

  // 计算下两个月的年份和月份
  const targetMonth = month + 2;
  const targetYear = year + Math.floor(targetMonth / 12);
  const newMonth = targetMonth % 12;

  // 返回下两个月的第一天
  return new Date(targetYear, newMonth, 0);
}

export function randomColor() {
  //获取随机颜色
  const colorLibrary = ['blue', 'green', 'orange', 'purple', 'red'];
  const randomIndex = Math.floor(Math.random() * colorLibrary.length);
  return colorLibrary[randomIndex];
}

export function applyDefaultOlStylesOnlyWhenMissing(html) {
  if (!html) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // 计算 <ol> 嵌套层级：最外层=1，子层=2...
  function getOlDepth(ol) {
    let depth = 1;
    let p = ol.parentElement;
    while (p) {
      if (p.tagName === 'OL') depth++;
      p = p.parentElement;
    }
    return depth;
  }

  // ✅ 你的默认层级规则
  function defaultListStyleTypeByDepth(depth) {
    if (depth === 1) return 'decimal';
    if (depth === 2) return 'lower-alpha';
    if (depth === 3) return 'lower-roman';
    if (depth === 4) return 'upper-alpha';
    return 'upper-roman'; // depth >= 5
  }

  // ✅ 已经显式设置过样式（手动选择/已有）的就不动
  function hasExplicitListStyle(ol) {
    // inline style 里有 list-style-type
    const styleAttr = ol.getAttribute('style') || '';
    if (/list-style-type\s*:/i.test(styleAttr)) return true;

    // DOM style 已经有值（兼容某些规范化场景）
    if (ol.style && ol.style.listStyleType) return true;

    // 有人会用原生 type="a|A|i|I|1" 来表达样式，也算显式
    if (ol.hasAttribute('type')) return true;

    return false;
  }

  doc.querySelectorAll('ol').forEach((ol) => {
    if (hasExplicitListStyle(ol)) return; // ✅ 用户设过就不覆盖

    const depth = getOlDepth(ol);
    const t = defaultListStyleTypeByDepth(depth);

    // ✅ 只补缺失的
    ol.style.listStyleType = t;
  });

  return doc.body.innerHTML;
}

export function addWorkingDays(startDate, days) {
  if (!days || days <= 0) {
    return new Date(startDate);
  }

  const date = new Date(startDate);
  let addedDays = 0;

  while (addedDays < days) {
    date.setDate(date.getDate() + 1);

    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    if (day !== 0 && day !== 6) {
      addedDays++;
    }
  }

  return date;
}

// =============================
// CKEditor Company Default Font
// =============================

const HF_ALLOWED_FONTS = new Set([
  'default',
  'Arial, Helvetica, sans-serif',
  'Calibri, sans-serif',
  'Courier New, Courier, monospace',
  'Georgia, serif',
  'Times New Roman, Times, serif',
  'Verdana, Geneva, sans-serif',
]);

const HF_FONT_ALIASES = {
  'arial': 'Arial, Helvetica, sans-serif',
  'calibri': 'Calibri, sans-serif',
  'courier new': 'Courier New, Courier, monospace',
  'georgia': 'Georgia, serif',
  'times new roman': 'Times New Roman, Times, serif',
  'verdana': 'Verdana, Geneva, sans-serif',
};

function hfNormalizeFontFamily(raw, fallback = 'default') {
  if (typeof raw !== 'string') return fallback;

  let v = raw.trim();
  if (!v) return fallback;

  if (!/^[a-zA-Z0-9 ,\-]+$/.test(v)) return fallback;

  const key = v.toLowerCase();
  if (HF_FONT_ALIASES[key]) v = HF_FONT_ALIASES[key];

  if (!HF_ALLOWED_FONTS.has(v)) return fallback;

  return v;
}

function hfNormalizeFontSize(raw, fallback = 'default') {
  if (!raw) return fallback;

  const m = String(raw).trim().toLowerCase().match(/^(\d{1,3})(px|pt)?$/);
  if (!m) return fallback;

  const n = parseInt(m[1], 10);
  if (isNaN(n) || n < 8 || n > 72) return fallback;

  return `${n}pt`;
}

function hfParseCompanyFontSetting(setting) {
  if (!setting || typeof setting !== 'string') {
    return { fontFamily: 'default', fontSize: 'default' };
  }

  const parts = setting.split('#');

  return {
    fontFamily: hfNormalizeFontFamily(parts[0], 'default'),
    fontSize: hfNormalizeFontSize(parts[1], 'default'),
  };
}

/**
 *统一 CKEditor 默认字体应用函数
 */
export function applyCompanyDefaultFont(editor, companySetting) {
  if (!editor) return;

  const { fontFamily, fontSize } = hfParseCompanyFontSetting(companySetting);

  if (fontFamily !== 'default') {
    editor.execute('fontFamily', { value: fontFamily });
  }

  if (fontSize !== 'default') {
    editor.execute('fontSize', { value: fontSize });
  }


  editor.model.document.selection.on('change:range', () => {
    const selection = editor.model.document.selection;

    if (!selection.hasAttribute('fontFamily') && fontFamily !== 'default') {
      editor.execute('fontFamily', { value: fontFamily });
    }

    if (!selection.hasAttribute('fontSize') && fontSize !== 'default') {
      editor.execute('fontSize', { value: fontSize });
    }
  });

  let initialized = false;

  editor.editing.view.document.on('focus', () => {
    if (initialized) return;
    initialized = true;

    const selection = editor.model.document.selection;

    if (!selection.hasAttribute('fontFamily') && fontFamily !== 'default') {
      editor.execute('fontFamily', { value: fontFamily });
    }

    if (!selection.hasAttribute('fontSize') && fontSize !== 'default') {
      editor.execute('fontSize', { value: fontSize });
    }
  });
}

// 打开 Public API 文档页面，通过隐藏表单 POST 提交 token，避免 token 暴露在 URL 中
export function openPublicApiDoc() {
  let token = localStorage.getItem('token');
  if (token) {
    let form = document.createElement('form');
    form.method = 'POST';
    form.action = config.publicApiUrl + '/swagger/auth';
    form.target = '_blank';

    let input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'token';
    input.value = token;
    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }
}
